import { useEffect, useMemo, useState } from 'react';
import { API_CONFIG, CREDIT_COSTS } from '@/services/api';
import { useAppStore } from '@/stores/appStore';
import { ScrapeButton } from '@/components/ScrapeButton';
import { Header } from '@/components';
import { enqueueScrapeJob } from '@/services/scrapeQueue';
import { addHotListEntityDb } from '@/services/hotLists';

type RephonicShow = {
  id?: string | number;
  uuid?: string;
  podcast_id?: string | number;
  title?: string;
  name?: string;
  publisher?: string;
  author?: string;
  description?: string;
  summary?: string;
  website?: string;
  site?: string;
  link?: string;
  rss?: string;
  rssUrl?: string;
  feedUrl?: string;
  image?: string;
  imageUrl?: string;
  artworkUrl?: string;
  artwork_url?: string;
  categories?: string[];
  socials?: Record<string, string[]>;
  [key: string]: any;
};

type RephonicEpisode = {
  id?: string;
  title?: string;
  published?: string;
  duration?: number;
  audio_url?: string;
  notes?: string;
  summary?: string;
  highlight?: string | null;
  podcast?: {
    id?: string;
    name?: string;
    artwork_thumbnail_url?: string;
    downloads_per_episode?: number;
    est_weekly_downloads?: number;
    [key: string]: any;
  };
  [key: string]: any;
};
type ShowEpisodeState = {
  items: RephonicEpisode[];
  loading: boolean;
  error?: string;
  page: number;
  more: boolean;
  query: string;
};
type EpisodeDetailState = {
  loading: boolean;
  error?: string;
  data?: any;
  transcript?: any;
};

type TargetShow = {
  showId: string;
  title: string;
  publisher?: string;
  website?: string;
  rss?: string;
  image?: string;
  raw: RephonicShow;
};

type IntelEntry = { data?: any; scrapedAt?: string };
type IntelMap = Record<string, Record<string, IntelEntry>>;
type CategoryOption = { value: string; label: string };

const LIST_ENTITY_TYPE = 'podcast';
const DEFAULT_LIST_STORAGE_KEY = 'profitibull_default_podcast_list';
const GUIDE_CONTEXT_STORAGE_KEY = 'pepai-guide-context-v1';

const asText = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (value && typeof value === 'object' && typeof (value as any).name === 'string') {
    return (value as any).name;
  }
  return '';
};

const formatNumber = (value: any) => {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  return num.toLocaleString();
};

const formatDate = (value: any) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
};

type FilterValues = {
  listenersMin: string;
  listenersMax: string;
  ratingsMin: string;
  ratingsMax: string;
  socialReachMin: string;
  socialReachMax: string;
  categoriesAll: string;
  categoriesAny: string;
  locationsAny: string;
  languagesAny: string;
  gendersAny: string;
  active: string;
  sponsored: string;
  sponsorsAny: string;
  professionsAny: string;
  professionsAll: string;
  interestsAny: string;
  interestsAll: string;
  guests: string;
  youtube: string;
  engagementMin: string;
  engagementMax: string;
  frequenciesAny: string;
  episodesMin: string;
  episodesMax: string;
  foundedMin: string;
  foundedMax: string;
  politicalAny: string;
  ageAny: string;
  ageAll: string;
  relationshipAny: string;
  educationAny: string;
  educationAll: string;
  incomeMin: string;
  incomeMax: string;
  crossPromos: string;
  branded: string;
  parents: string;
};

const EMPTY_FILTERS: FilterValues = {
  listenersMin: '',
  listenersMax: '',
  ratingsMin: '',
  ratingsMax: '',
  socialReachMin: '',
  socialReachMax: '',
  categoriesAll: '',
  categoriesAny: '',
  locationsAny: '',
  languagesAny: '',
  gendersAny: '',
  active: '',
  sponsored: '',
  sponsorsAny: '',
  professionsAny: '',
  professionsAll: '',
  interestsAny: '',
  interestsAll: '',
  guests: '',
  youtube: '',
  engagementMin: '',
  engagementMax: '',
  frequenciesAny: '',
  episodesMin: '',
  episodesMax: '',
  foundedMin: '',
  foundedMax: '',
  politicalAny: '',
  ageAny: '',
  ageAll: '',
  relationshipAny: '',
  educationAny: '',
  educationAll: '',
  incomeMin: '',
  incomeMax: '',
  crossPromos: '',
  branded: '',
  parents: '',
};

const FREQUENCY_VALUES = ['infrequently', 'monthly', 'twicemonthly', 'weekly', 'twiceweekly', 'daily'];
const POLITICAL_VALUES = ['left', 'center_left', 'center', 'center_right', 'right'];
const AGE_GROUP_VALUES = ['under_18', '18_24', '25_34', '35_44', '45_54', '55_64', '65_plus'];
const RELATIONSHIP_VALUES = ['single', 'relationship', 'married'];
const EDUCATION_VALUES = ['less_than_high_school', 'high_school', 'bachelors_degree', 'masters_degree', 'advanced_degree'];

const compactPodcastFilters = (filters: FilterValues): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters) as Array<[keyof FilterValues, string]>) {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) continue;
    if (trimmed.includes(',')) {
      const list = trimmed
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);
      out[key] = list.length > 0 ? list : trimmed;
      continue;
    }
    out[key] = trimmed;
  }
  return out;
};

const stripHtml = (input?: string) => {
  if (!input) return '';
  return input.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};

const normalizeImageUrl = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const raw = value.trim();
  if (!raw) return '';
  if (raw.startsWith('//')) return `https:${raw}`;
  if (raw.startsWith('http://')) return raw.replace(/^http:\/\//, 'https://');
  if (raw.startsWith('https://')) return raw;
  return '';
};

const coerceImageValue = (value: unknown): string => {
  if (!value) return '';
  if (typeof value === 'string') return normalizeImageUrl(value);
  if (Array.isArray(value)) {
    for (const entry of value) {
      const resolved = coerceImageValue(entry);
      if (resolved) return resolved;
    }
    return '';
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const candidates = [
      obj.url,
      obj.src,
      obj.href,
      obj.image,
      obj.imageUrl,
      obj.thumbnail,
      obj.thumbnailUrl,
      obj.artwork_thumbnail_url,
      obj.artworkThumbnailUrl,
      obj.artwork_thumbnail,
      obj.artworkThumbnail,
      obj.artwork,
      obj.artworkUrl,
      obj.artwork_url,
    ];
    for (const candidate of candidates) {
      const resolved = coerceImageValue(candidate);
      if (resolved) return resolved;
    }
  }
  return '';
};

const summarizeWebsiteData = (data: unknown): {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  domains?: string[];
  phones?: string[];
  address?: string;
  socials?: Record<string, string[]>;
  keywords?: string[];
  sample?: string;
  pagesCount: number;
} => {
  const pages = Array.isArray(data) ? data : [];
  const first = pages[0] as any;
  const meta = first?.metadata || {};
  const jsonLd = Array.isArray(meta?.jsonLd) ? meta.jsonLd : [];
  const graph = jsonLd
    .flatMap((node: any) => (node && node['@graph'] ? node['@graph'] : node))
    .filter(Boolean);
  const org =
    graph.find(
      (n: any) =>
        n && (n['@type'] === 'Organization' || n['@type'] === 'Corporation')
    ) || {};
  const canonicalUrl = meta?.canonicalUrl || org?.url || first?.url || '';
  const description = meta?.description || org?.description || '';
  const title = meta?.title || org?.name || '';
  const address = org?.address
    ? [
        org.address?.streetAddress,
        org.address?.addressLocality,
        org.address?.addressRegion,
        org.address?.postalCode,
        org.address?.addressCountry,
      ]
        .filter(Boolean)
        .join(', ')
    : '';
  const contactPoints = Array.isArray(org?.contactPoint)
    ? org.contactPoint
    : [];
  const phones = contactPoints
    .flatMap((cp: any) =>
      Array.isArray(cp?.telephone)
        ? cp.telephone
        : cp?.telephone
          ? [cp.telephone]
          : []
    )
    .filter(Boolean);
  const sameAs = Array.isArray(org?.sameAs) ? org.sameAs : [];
  const socials: Record<string, string[]> = {
    linkedin: [],
    facebook: [],
    instagram: [],
    tiktok: [],
    youtube: [],
    x: [],
    podcast: [],
  };
  for (const url of sameAs) {
    if (typeof url !== 'string') continue;
    if (url.includes('linkedin.com')) socials.linkedin.push(url);
    else if (url.includes('facebook.com')) socials.facebook.push(url);
    else if (url.includes('instagram.com')) socials.instagram.push(url);
    else if (url.includes('tiktok.com')) socials.tiktok.push(url);
    else if (url.includes('youtube.com') || url.includes('youtu.be'))
      socials.youtube.push(url);
    else if (url.includes('x.com') || url.includes('twitter.com'))
      socials.x.push(url);
    else if (
      url.includes('podcast') ||
      url.includes('podbean') ||
      url.includes('spotify') ||
      url.includes('apple.com')
    )
      socials.podcast.push(url);
  }
  const keywords = Array.isArray(meta?.keywords)
    ? meta.keywords
    : typeof meta?.keywords === 'string'
      ? meta.keywords.split(',').map((k: string) => k.trim()).filter(Boolean)
      : [];
  const sample = typeof first?.text === 'string' ? first.text.slice(0, 420) : '';
  const domains = canonicalUrl ? [canonicalUrl] : [];
  return {
    title,
    description,
    canonicalUrl,
    domains,
    phones,
    address,
    socials,
    keywords,
    sample,
    pagesCount: pages.length,
  };
};

const summarizePodcastData = (data: unknown): {
  showTitle?: string;
  publisher?: string;
  description?: string;
  website?: string;
  rssUrl?: string;
  image?: string;
  latestEpisode?: {
    title?: string;
    duration?: string;
    episodeUrl?: string;
    description?: string;
  };
  episodes: Array<{
    title?: string;
    duration?: string;
    episodeUrl?: string;
    description?: string;
  }>;
} => {
  const items = Array.isArray(data) ? data : [];
  const cleaned = items.filter((item) => !(item && typeof item === 'object' && 'warning' in (item as Record<string, unknown>)));
  const first = (cleaned[0] as Record<string, any>) || {};
  const latestEpisode = first.latestEpisode;
  const episodes = Array.isArray(first.episodes) ? first.episodes : [];
  return {
    showTitle: first.showTitle,
    publisher: first.publisher,
    description: first.description,
    website: first.website,
    rssUrl: first.rssUrl,
    image: first.image,
    latestEpisode,
    episodes,
  };
};

const summarizeLinkedinData = (data: unknown): {
  name?: string;
  description?: string;
  industry?: string;
  website?: string;
  headquarters?: string;
  employeeCount?: number;
  size?: string;
  followers?: string | number;
  logo?: string;
  posts: Array<{
    date?: string;
    text?: string;
    url?: string;
    likes?: number | string;
    comments?: number | string;
    shares?: number | string;
    views?: number | string;
  }>;
  specialties: string[];
  similar: Array<{ name?: string; link?: string }>;
  employees: Array<{ name?: string; link?: string; image?: string }>;
} => {
  const items = Array.isArray(data) ? data : [];
  const first = (items[0] as Record<string, any>) || {};
  const getStr = (v: any) => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
  const name = getStr(first.name || first.companyName || first.title);
  const description = getStr(first.description || first.about);
  const industry = getStr(first.industry);
  const website = getStr(first.website || first.domain);
  const headquarters = getStr(first.headquarters || first.location?.city);
  const employeeCount = typeof first.employeeCount === 'number' ? first.employeeCount : undefined;
  const size = getStr(first.size);
  const followers = first.followers ?? first.followersCount ?? undefined;
  const logo = getStr(first.logo || first.logoUrl);
  const posts = Array.isArray(first.posts)
    ? first.posts.slice(0, 8).map((p: any) => ({
        date: getStr(p.datePublished || p.date),
        text: getStr(p.text || p.caption),
        url: getStr(p.url || p.postUrl),
        likes: p.likes ?? p.likeCount ?? p.reactions ?? p.reactionCount ?? undefined,
        comments: p.comments ?? p.commentCount ?? undefined,
        shares: p.shares ?? p.shareCount ?? undefined,
        views: p.views ?? p.viewCount ?? undefined,
      }))
    : [];
  const specialties = Array.isArray(first.specialties) ? first.specialties.slice(0, 10) : [];
  const similar = Array.isArray(first.similarPages)
    ? first.similarPages.slice(0, 8).map((s: any) => ({
        name: getStr(s.name),
        link: getStr(s.link || s.url),
      }))
    : [];
  const employees = Array.isArray(first.employees)
    ? first.employees.slice(0, 8).map((e: any) => ({
        name: getStr(e.name),
        link: getStr(e.link || e.url),
        image: getStr(e.image || e.avatar),
      }))
    : [];
  return {
    name,
    description,
    industry,
    website,
    headquarters,
    employeeCount,
    size,
    followers,
    logo,
    posts,
    specialties,
    similar,
    employees,
  };
};

const summarizeFacebookData = (data: unknown): {
  title?: string;
  pageUrl?: string;
  likes?: number | string;
  followers?: number | string;
  category?: string;
  phone?: string;
  website?: string;
  address?: string;
  intro?: string;
  profilePictureUrl?: string;
  coverPhotoUrl?: string;
  posts: Array<{
    date?: string;
    text?: string;
    url?: string;
    likes?: number | string;
    comments?: number | string;
    shares?: number | string;
    views?: number | string;
    image?: string;
    video?: string;
  }>;
} => {
  const payload = (data && typeof data === 'object') ? (data as Record<string, any>) : {};
  const items = Array.isArray(data)
    ? data
    : Array.isArray(payload.items)
      ? payload.items
      : [];
  const pageArray = Array.isArray(payload.page) ? payload.page : [];
  const postsArray = Array.isArray(payload.posts) ? payload.posts : [];
  const getStr = (v: any) => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
  const pageItem =
    pageArray[0] ||
    items.find((i: any) => i && (i.pageName || i.pageUrl || i.title || i.facebookUrl));
  const postItems =
    postsArray.length > 0
      ? postsArray
      : items.filter((i: any) => i && (i.post_id || i.post_url || i.text));
  const page = (pageItem as Record<string, any>) || {};

  const title = getStr(page.pageName || page.title || page.facebookUrl);
  const pageUrl = getStr(page.pageUrl || page.facebookUrl);
  const likes = page.likes ?? undefined;
  const followers = page.followers ?? undefined;
  const category = getStr(page.category || (Array.isArray(page.categories) ? page.categories.join(', ') : ''));
  const phone = getStr(page.phone);
  const website = getStr(page.website);
  const address = getStr(page.address);
  const intro = getStr(page.intro);
  const profilePictureUrl = getStr(page.profilePictureUrl);
  const coverPhotoUrl = getStr(page.coverPhotoUrl);

  const posts = postItems.slice(0, 10).map((p: any) => ({
    date: p.create_time ? new Date(p.create_time).toISOString() : '',
    text: getStr(p.text),
    url: getStr(p.post_url || p.postUrl || p.input),
    likes: p.like_count ?? p.likes ?? undefined,
    comments: p.comment_count ?? p.comments ?? undefined,
    shares: p.share_count ?? p.shares ?? undefined,
    views: p.view_count ?? p.play_count ?? undefined,
    image: Array.isArray(p.image_list) ? p.image_list[0] : undefined,
    video: Array.isArray(p.video_list) ? p.video_list[0] : undefined,
  }));

  return {
    title,
    pageUrl,
    likes,
    followers,
    category,
    phone,
    website,
    address,
    intro,
    profilePictureUrl,
    coverPhotoUrl,
    posts,
  };
};

const summarizeInstagramData = (data: unknown): {
  username?: string;
  fullName?: string;
  bio?: string;
  website?: string;
  followers?: number | string;
  following?: number | string;
  postsCount?: number | string;
  profileUrl?: string;
  avatar?: string;
  isVerified?: boolean;
  recentPosts: Array<{
    caption?: string;
    url?: string;
    likes?: number | string;
    comments?: number | string;
    date?: string;
    image?: string;
    video?: string;
    views?: number | string;
  }>;
} => {
  const items = Array.isArray(data) ? data : data && typeof data === 'object' ? [data] : [];
  const getStr = (v: any) =>
    typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
  const first = (items[0] as Record<string, any>) || {};

  const profile = first;

  const username = getStr(profile.username || profile.userName || profile.handle);
  const fullName = getStr(profile.fullName || profile.name || profile.nickName);
  const bio = getStr(profile.biography || profile.bio);
  const website = getStr(profile.externalUrl || profile.externalUrlShimmed || profile.website);
  const followers = profile.followersCount ?? profile.followers ?? undefined;
  const following = profile.followsCount ?? profile.following ?? undefined;
  const postsCount = profile.postsCount ?? profile.posts ?? profile.igtvVideoCount ?? undefined;
  const profileUrl = getStr(profile.url || profile._discovered_profile_url || profile.inputUrl);
  const avatar = getStr(profile.profilePicUrlHD || profile.profilePicUrl || profile.avatar || profile.profilePicture);
  const isVerified = Boolean(profile.verified || profile.isVerified);

  const postSource = Array.isArray(profile.latestPosts) && profile.latestPosts.length > 0
    ? profile.latestPosts
    : Array.isArray(profile.latestIgtvVideos) && profile.latestIgtvVideos.length > 0
      ? profile.latestIgtvVideos
      : [];
  const recentPosts = postSource.slice(0, 8).map((p: any) => ({
    caption: getStr(p.caption || p.text || p.description),
    url: getStr(p.url || p.postUrl),
    likes: p.likesCount ?? p.likes ?? p.likeCount ?? undefined,
    comments: p.commentsCount ?? p.comments ?? p.commentCount ?? undefined,
    views: p.videoViewCount ?? undefined,
    date: getStr(p.timestamp || p.date),
    image: Array.isArray(p.images) ? p.images[0] : p.displayUrl || p.imageUrl || p.thumbnail,
    video: p.videoUrl || p.video,
  }));

  return {
    username,
    fullName,
    bio,
    website,
    followers,
    following,
    postsCount,
    profileUrl,
    avatar,
    isVerified,
    recentPosts,
  };
};

const summarizeTikTokData = (data: unknown): {
  username?: string;
  fullName?: string;
  bio?: string;
  website?: string;
  followers?: number | string;
  following?: number | string;
  videos?: number | string;
  hearts?: number | string;
  profileUrl?: string;
  avatar?: string;
  isVerified?: boolean;
  recentPosts: Array<{
    caption?: string;
    url?: string;
    likes?: number | string;
    comments?: number | string;
    shares?: number | string;
    views?: number | string;
    date?: string;
    image?: string;
    duration?: number | string;
    hashtags?: string[];
  }>;
} => {
  const items = Array.isArray(data) ? data : [];
  const getStr = (v: any) =>
    typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
  const first = (items[0] as Record<string, any>) || {};
  const meta = first.authorMeta || {};

  const username = getStr(meta.name || meta.username);
  const fullName = getStr(meta.nickName || meta.name);
  const bio = getStr(meta.signature);
  const website = getStr(meta.bioLink);
  const followers = meta.fans ?? undefined;
  const following = meta.following ?? undefined;
  const videos = meta.video ?? undefined;
  const hearts = meta.heart ?? undefined;
  const profileUrl = getStr(meta.profileUrl || first._discovered_profile_url);
  const avatar = getStr(meta.originalAvatarUrl || meta.avatar);
  const isVerified = Boolean(meta.verified);

  const recentPosts = items.slice(0, 8).map((p: any) => ({
    caption: getStr(p.text),
    url: getStr(p.webVideoUrl),
    likes: p.diggCount ?? undefined,
    comments: p.commentCount ?? undefined,
    shares: p.shareCount ?? undefined,
    views: p.playCount ?? undefined,
    date: getStr(p.createTimeISO),
    image: p.videoMeta?.coverUrl || p.videoMeta?.originalCoverUrl,
    duration: p.videoMeta?.duration ?? undefined,
    hashtags: Array.isArray(p.hashtags) ? p.hashtags.map((h: any) => getStr(h.name)).filter(Boolean) : [],
  }));

  return {
    username,
    fullName,
    bio,
    website,
    followers,
    following,
    videos,
    hearts,
    profileUrl,
    avatar,
    isVerified,
    recentPosts,
  };
};

const summarizeYouTubeData = (data: unknown): {
  channelName?: string;
  channelUrl?: string;
  channelAvatar?: string;
  subscribers?: number | string;
  totalViews?: number | string;
  totalVideos?: number | string;
  channelLocation?: string;
  recentVideos: Array<{
    title?: string;
    url?: string;
    date?: string;
    views?: number | string;
    likes?: number | string;
    comments?: number | string;
    thumbnail?: string;
    text?: string;
  }>;
} => {
  const items = Array.isArray(data) ? data : [];
  const cleaned = items.filter((item: any) => {
    const note = String(item?.note || '').toLowerCase();
    const error = String(item?.error || '');
    return error !== 'NO_RESULTS' && !note.includes('no results were collected');
  });
  const first = (cleaned[0] as Record<string, any>) || {};
  const about = first.aboutChannelInfo || {};
  const channelName = about.channelName || first.channelName;
  const channelUrl = about.channelUrl || first.channelUrl;
  const channelAvatar = about.channelAvatarUrl || first.channelAvatarUrl;
  const subscribers = about.numberOfSubscribers || first.numberOfSubscribers;
  const totalViews = about.channelTotalViews || first.channelTotalViews;
  const totalVideos = about.channelTotalVideos || first.channelTotalVideos;
  const channelLocation = about.channelLocation || first.channelLocation;
  const recentVideos = cleaned.slice(0, 8).map((v: any) => ({
    title: v.title,
    url: v.url,
    date: v.date,
    views: v.viewCount,
    likes: v.likes,
    comments: v.commentsCount,
    thumbnail: v.thumbnailUrl,
    text: v.text,
  }));
  return {
    channelName,
    channelUrl,
    channelAvatar,
    subscribers,
    totalViews,
    totalVideos,
    channelLocation,
    recentVideos,
  };
};

const summarizeXData = (data: unknown): {
  profileName?: string;
  profileHandle?: string;
  profileUrl?: string;
  avatar?: string;
  bio?: string;
  followers?: number | string;
  following?: number | string;
  recentPosts: Array<{
    text?: string;
    url?: string;
    date?: string;
    likes?: number | string;
    replies?: number | string;
    reposts?: number | string;
    quotes?: number | string;
    views?: number | string;
    media?: string;
  }>;
} => {
  const raw = data as Record<string, any> | undefined;
  const items = Array.isArray(data)
    ? data
    : Array.isArray(raw?.results)
      ? raw?.results
      : Array.isArray(raw?.data)
        ? raw?.data
        : Array.isArray(raw?.items)
          ? raw?.items
          : [];
  const first = (items[0] as Record<string, any>) || {};
  const author = first.author || first.user || {};
  const profileName = author.name || first.authorName || first.userName;
  const profileHandle = author.screenName || author.userName || author.username || first.authorUsername;
  const profileUrl = first._discovered_profile_url || author.profileUrl || first.authorProfileUrl;
  const avatar = author.profileImageUrl || author.profileImageUrlHttps || first.authorProfileImageUrl || first.profileImageUrl;
  const bio = author.description || first.authorDescription;
  const followers = author.followersCount || author.followers || undefined;
  const following = author.friendsCount || author.following || undefined;
  const recentPosts = items.slice(0, 10).map((p: any) => ({
    text: p.postText || p.fullText || p.text,
    url: p.postUrl || p.url || p.tweetUrl,
    date: p.createdAt || p.date || p.timestamp,
    likes: p.favouriteCount || p.favoriteCount || p.likeCount,
    replies: p.replyCount,
    reposts: p.repostCount || p.retweetCount,
    quotes: p.quoteCount,
    views: p.viewCount || p.impressionCount,
    media: Array.isArray(p.media) && p.media[0] ? (p.media[0].mediaUrlHttps || p.media[0].mediaUrl) : undefined,
  }));
  return {
    profileName,
    profileHandle,
    profileUrl,
    avatar,
    bio,
    followers,
    following,
    recentPosts,
  };
};

function normalizeShowId(show: RephonicShow) {
  const candidates = [show.id, show.uuid, show.podcast_id, show.podcastId, show.slug];
  for (const v of candidates) {
    if (v != null && String(v).trim()) return String(v);
  }
  return String(asText(show.title) || asText(show.name) || 'unknown');
}

function normalizeEpisodeId(ep: RephonicEpisode, fallbackIndex = 0) {
  const candidates = [ep.id, (ep as any).guid, (ep as any).itunes_id, (ep as any).episode_id, ep.audio_url, ep.title];
  for (const v of candidates) {
    if (v != null && String(v).trim()) return String(v);
  }
  return `episode-${fallbackIndex}`;
}

const showImageUrl = (show: RephonicShow | Record<string, any> | undefined | null) => {
  if (!show || typeof show !== 'object') return '';
  const candidates = [
    (show as any).image,
    (show as any).imageUrl,
    (show as any).image_url,
    (show as any).artwork_thumbnail_url,
    (show as any).artworkThumbnailUrl,
    (show as any).artwork_thumbnail,
    (show as any).artworkThumbnail,
    (show as any).artworkUrl,
    (show as any).artwork_url,
    (show as any).artwork,
    (show as any).thumbnail,
    (show as any).thumbnail_url,
    (show as any).raw?.image,
    (show as any).raw?.imageUrl,
    (show as any).raw?.image_url,
    (show as any).raw?.artwork_thumbnail_url,
    (show as any).raw?.artworkThumbnailUrl,
    (show as any).raw?.artwork_thumbnail,
    (show as any).raw?.artworkThumbnail,
    (show as any).raw?.artworkUrl,
    (show as any).raw?.artwork_url,
    (show as any).raw?.artwork,
    (show as any).podcast?.image,
    (show as any).podcast?.imageUrl,
    (show as any).podcast?.artworkUrl,
    (show as any).podcast?.artwork_url,
    (show as any).images,
    (show as any).artwork,
    (show as any).thumbnail,
  ];
  for (const candidate of candidates) {
    const normalized = coerceImageValue(candidate);
    if (normalized) return normalized;
  }
  return '';
};

const pickMetric = (show: RephonicShow, keys: string[]) => {
  for (const key of keys) {
    const direct = (show as any)?.[key];
    if (direct !== undefined && direct !== null && direct !== '') return direct;
    const meta = (show as any)?.metrics?.[key];
    if (meta !== undefined && meta !== null && meta !== '') return meta;
    const stats = (show as any)?.stats?.[key];
    if (stats !== undefined && stats !== null && stats !== '') return stats;
  }
  return null;
};

const readPath = (obj: any, path: string) => {
  return path.split('.').reduce((acc: any, key) => (acc == null ? undefined : acc[key]), obj);
};

const pickValue = (obj: any, paths: string[]) => {
  for (const path of paths) {
    const value = readPath(obj, path);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
};

const showPublisher = (show: any): string =>
  String(
    pickValue(show, [
      'publisher.name',
      'publisher.display_name',
      'publisher.title',
      'publisher',
      'author.name',
      'author',
      'host.name',
      'host',
      'owner.name',
      'owner_name',
      'network',
    ]) ?? ''
  );

const showWebsite = (show: any): string =>
  String(pickValue(show, ['web_url', 'website', 'site', 'link', 'podcast.web_url', 'podcast.website']) ?? '');

const showRss = (show: any): string =>
  String(pickValue(show, ['feed_url', 'rss', 'rssUrl', 'feedUrl', 'podcast.feed_url', 'podcast.rss']) ?? '');

const toArray = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  return [];
};

const formatClock = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00';
  const s = Math.floor(seconds % 60);
  const m = Math.floor((seconds / 60) % 60);
  const h = Math.floor(seconds / 3600);
  if (h > 0) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

function EpisodeInsights({
  source,
  transcript,
  showRaw = false,
  onViewDetails,
}: {
  source: any;
  transcript?: any;
  showRaw?: boolean;
  onViewDetails?: (episodeId: string) => void;
}) {
  const ep = source?.episode ?? source ?? {};
  const title = String(pickValue(ep, ['title']) ?? 'Episode');
  const podcastName = String(pickValue(ep, ['podcast.name', 'show.name', 'podcast_name']) ?? 'Unknown show');
  const publisher = String(
    pickValue(ep, ['podcast.publisher.name', 'podcast.publisher', 'publisher.name', 'publisher']) ?? 'Unknown publisher'
  );
  const description = String(pickValue(ep, ['notes', 'description', 'summary']) ?? '');
  const published = pickValue(ep, ['published', 'date']);
  const duration = Number(pickValue(ep, ['duration']) ?? 0);
  const topics = toArray(pickValue(ep, ['topics']));
  const summary = String(pickValue(ep, ['summary']) ?? '');
  const hosts = toArray(pickValue(ep, ['hosts', 'people.hosts']));
  const guests = toArray(pickValue(ep, ['guests', 'people.guests']));
  const crossPromotions = toArray(pickValue(ep, ['cross_promos', 'crossPromotions']));
  const keyMoments = toArray(pickValue(ep, ['key_moments', 'keyMoments']));
  const qa = toArray(pickValue(ep, ['questions', 'questions_answers', 'questionsAndAnswers']));
  const format = String(pickValue(ep, ['type', 'format']) ?? '');
  const tone = toArray(pickValue(ep, ['tone']));
  const evergreen = pickValue(ep, ['evergreen']);
  const podcastArtwork = String(
    pickValue(ep, ['podcast.artwork_thumbnail_url', 'podcast.artwork_url', 'podcast.image', 'podcast.image_url']) ?? ''
  );
  const episodeId = String(pickValue(ep, ['id']) ?? '');
  const audioUrl = String(pickValue(ep, ['audio_url']) ?? '');
  const formatDurationText = duration > 0 ? `${formatClock(duration)} (${duration.toLocaleString()} sec)` : 'No duration';

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-indigo-900/40 to-gray-900 border border-indigo-700/40 rounded-xl p-4">
        <div className="flex gap-4 items-start">
          <div className="w-16 h-16 rounded-lg bg-black/30 overflow-hidden shrink-0">
            {podcastArtwork ? <img src={podcastArtwork} alt="" className="w-full h-full object-cover" /> : null}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-wide text-indigo-200/80">{podcastName}</div>
            <div className="text-xs text-gray-400 mt-0.5">{publisher}</div>
            <div className="text-sm font-semibold text-white mt-2">{title}</div>
            <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-300">
              <span className="px-2 py-0.5 rounded bg-black/30 border border-gray-700/50">
                {published ? formatDate(published) : 'No date'}
              </span>
              <span className="px-2 py-0.5 rounded bg-black/30 border border-gray-700/50">{formatDurationText}</span>
              <span className="px-2 py-0.5 rounded bg-black/30 border border-gray-700/50">Format: {format || '—'}</span>
              <span className="px-2 py-0.5 rounded bg-black/30 border border-gray-700/50">
                Evergreen: {evergreen === undefined || evergreen === null || evergreen === '' ? '—' : String(evergreen)}
              </span>
            </div>
            <div className="mt-2 flex items-center gap-3">
              {audioUrl ? (
                <a href={audioUrl} target="_blank" rel="noreferrer" className="text-xs text-sky-300 hover:underline">
                  Open audio
                </a>
              ) : null}
              {episodeId && onViewDetails ? (
                <button className="text-xs text-indigo-300 hover:underline" onClick={() => onViewDetails(episodeId)}>
                  View details
                </button>
              ) : null}
            </div>
          </div>
        </div>
        {description && <div className="mt-3 text-sm text-gray-200 whitespace-pre-wrap">{stripHtml(description)}</div>}
      </div>

      {(topics.length > 0 || summary) && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <div className="text-xs uppercase text-gray-500 mb-2">Topics & Summary</div>
          {topics.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {topics.slice(0, 20).map((topic: any, i: number) => (
                <span key={`topic-${i}`} className="px-2 py-0.5 rounded bg-emerald-500/20 border border-emerald-400/40 text-[11px] text-emerald-200">
                  {typeof topic === 'string' ? topic : String(topic?.name || topic?.topic || 'Topic')}
                </span>
              ))}
            </div>
          )}
          {summary && <div className="text-sm text-gray-300 whitespace-pre-wrap">{stripHtml(summary)}</div>}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <div className="text-xs uppercase text-gray-500 mb-2">Hosts</div>
          {hosts.length === 0 ? (
            <div className="text-sm text-gray-400">No host data.</div>
          ) : (
            <div className="space-y-2">
              {hosts.slice(0, 10).map((host: any, i: number) => (
                <div key={`host-${i}`} className="text-sm text-gray-200 border border-gray-700/50 rounded p-2">
                  <div>{host?.name || host?.full_name || String(host)}</div>
                  {host?.description ? <div className="text-xs text-gray-400 mt-1">{host.description}</div> : null}
                  {host?.affiliation ? <div className="text-xs text-indigo-200 mt-1">{host.affiliation}</div> : null}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <div className="text-xs uppercase text-gray-500 mb-2">Guests</div>
          {guests.length === 0 ? (
            <div className="text-sm text-gray-400">No guest data.</div>
          ) : (
            <div className="space-y-2">
              {guests.slice(0, 10).map((guest: any, i: number) => (
                <div key={`guest-${i}`} className="text-sm text-gray-200 border border-gray-700/50 rounded p-2">
                  <div>{guest?.name || guest?.full_name || String(guest)}</div>
                  {guest?.description ? <div className="text-xs text-gray-400 mt-1">{guest.description}</div> : null}
                  {guest?.affiliation ? <div className="text-xs text-indigo-200 mt-1">{guest.affiliation}</div> : null}
                  {guest?.industry ? <div className="text-xs text-gray-500 mt-1">{guest.industry}</div> : null}
                  {guest?.purpose ? <div className="text-xs text-gray-400 mt-1">Focus: {guest.purpose}</div> : null}
                  {guest?.alignment ? <div className="text-xs text-gray-400 mt-1">Relevance: {guest.alignment}</div> : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {keyMoments.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <div className="text-xs uppercase text-gray-500 mb-2">Key Moments</div>
          <div className="space-y-2">
            {keyMoments.slice(0, 12).map((m: any, i: number) => (
              <div key={`km-${i}`} className="text-sm text-gray-200">
                <span className="text-gray-400 mr-2">{formatClock(Number(m?.timestamp ?? m?.time ?? 0))}</span>
                {m?.text || m?.quote || String(m)}
              </div>
            ))}
          </div>
        </div>
      )}

      {qa.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <div className="text-xs uppercase text-gray-500 mb-2">Questions & Answers</div>
          <div className="space-y-3">
            {qa.slice(0, 8).map((q: any, i: number) => (
              <div key={`qa-${i}`} className="border border-gray-700/50 rounded p-2">
                <div className="text-sm text-indigo-200">{q?.question || q?.q || 'Question'}</div>
                <div className="text-sm text-gray-300 mt-1">{q?.answer || q?.a || ''}</div>
                {(q?.timestamp ?? q?.time) ? (
                  <div className="text-xs text-gray-500 mt-1">{formatClock(Number(q?.timestamp ?? q?.time ?? 0))}</div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {(crossPromotions.length > 0 || tone.length > 0) && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <div className="text-xs uppercase text-gray-500 mb-2">More Details</div>
          {tone.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {tone.map((t: any, i: number) => (
                <span key={`tone-${i}`} className="px-2 py-0.5 rounded bg-slate-500/20 border border-slate-400/40 text-[11px] text-slate-200">
                  {typeof t === 'string' ? t : String(t)}
                </span>
              ))}
            </div>
          )}
          {crossPromotions.length > 0 && (
            <div className="space-y-1">
              {crossPromotions.slice(0, 10).map((cp: any, i: number) => (
                <div key={`cp-${i}`} className="text-sm text-gray-300">
                  {cp?.name || cp?.title || String(cp)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showRaw && (
        <>
          <details className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <summary className="cursor-pointer text-xs uppercase text-gray-500">Raw Episode JSON</summary>
            <pre className="text-xs text-gray-300 overflow-auto mt-3">{JSON.stringify(ep, null, 2)}</pre>
          </details>
          <details className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <summary className="cursor-pointer text-xs uppercase text-gray-500">Raw Transcript JSON</summary>
            <pre className="text-xs text-gray-300 overflow-auto mt-3">{JSON.stringify(transcript ?? {}, null, 2)}</pre>
          </details>
        </>
      )}
    </div>
  );
}

export function BookingAIgent() {
  const { userEmail } = useAppStore();
  const [query, setQuery] = useState('');
  const [searchEntity, setSearchEntity] = useState<'podcasts' | 'episodes'>('podcasts');
  const [searchMode, setSearchMode] = useState<'topics' | 'titles' | 'publishers'>('topics');
  const [filters, setFilters] = useState<FilterValues>({ ...EMPTY_FILTERS });
  const [results, setResults] = useState<RephonicShow[]>([]);
  const [episodeResults, setEpisodeResults] = useState<RephonicEpisode[]>([]);
  const [rawResults, setRawResults] = useState<any>(null);
  const [targets, setTargets] = useState<TargetShow[]>([]);
  const [userLists, setUserLists] = useState<string[]>([]);
  const [activeListName, setActiveListName] = useState(
    () => localStorage.getItem(DEFAULT_LIST_STORAGE_KEY) || 'Podcast_default'
  );
  const [createListModalOpen, setCreateListModalOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [loading, setLoading] = useState(false);
  const [drawer, setDrawer] = useState<{ showId: string; title: string; source: string; data: any } | null>(null);
  const [intel, setIntel] = useState<IntelMap>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [savedSearches, setSavedSearches] = useState<any[]>([]);
  const [activeSearchId, setActiveSearchId] = useState<string | null>(null);
  const [searchMessage, setSearchMessage] = useState<string>('');
  const [resultsPage, setResultsPage] = useState(1);
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  const [countryOptions, setCountryOptions] = useState<string[]>([]);
  const [professionOptions, setProfessionOptions] = useState<string[]>([]);
  const [interestOptions, setInterestOptions] = useState<string[]>([]);
  const [showEpisodes, setShowEpisodes] = useState<Record<string, ShowEpisodeState>>({});
  const [episodeDetails, setEpisodeDetails] = useState<Record<string, EpisodeDetailState>>({});
  const [activeEpisodeId, setActiveEpisodeId] = useState<string | null>(null);
  const [showArtworkById, setShowArtworkById] = useState<Record<string, string>>({});
  const [inlineEpisodeIdByShow, setInlineEpisodeIdByShow] = useState<Record<string, string | null>>({});
  const [selectedPodcastIds, setSelectedPodcastIds] = useState<Set<string>>(new Set());
  const [selectedEpisodeIds, setSelectedEpisodeIds] = useState<Set<string>>(new Set());
  const [selectedTargetIds, setSelectedTargetIds] = useState<Set<string>>(new Set());

  const addPodcastToHotList = async (show: RephonicShow, showAlert = true) => {
    if (!userEmail) return;
    const showId = normalizeShowId(show);
    const title = asText(show.title) || asText(show.name) || 'Unknown Podcast';
    const publisher = showPublisher(show);
    const url = showWebsite(show);
    await addHotListEntityDb(userEmail, {
      id: `podcast:${showId}`,
      title,
      subtitle: publisher || undefined,
      source: 'podcast_search',
      platform: 'podcast',
      url: url || undefined,
      payload: {
        podcastId: showId,
        downloadsPerEpisode: pickMetric(show, ['downloads_per_episode', 'downloadsPerEpisode']),
        weeklyDownloads: pickMetric(show, ['est_weekly_downloads', 'estWeeklyDownloads', 'weekly_downloads']),
      },
    });
    if (showAlert) alert('Added to Hot List');
  };

  const addEpisodeToHotList = async (ep: RephonicEpisode, showAlert = true) => {
    if (!userEmail) return;
    const epId = String(ep.id || ep.guid || ep.itunes_id || Math.random().toString(36).slice(2));
    const title = asText(ep.title) || 'Unknown Episode';
    const podcastName = asText(ep.podcast?.name) || '';
    const url = asText(ep.audio_url) || '';
    await addHotListEntityDb(userEmail, {
      id: `episode:${epId}`,
      title,
      subtitle: podcastName || undefined,
      source: 'podcast_episode',
      platform: 'podcast',
      url: url || undefined,
      payload: {
        published: ep.published,
        duration: ep.duration,
        podcastId: ep.podcast?.id,
      },
    });
    if (showAlert) alert('Added to Hot List');
  };

  const bookingApi = API_CONFIG.BOOKING_API;
  const ecomApi = API_CONFIG.ECOM_ORCHESTRATOR_API;

  const splitEscapedList = (value: string): string[] => {
    const out: string[] = [];
    let current = '';
    for (let i = 0; i < value.length; i += 1) {
      const ch = value[i];
      if (ch === '\\' && i + 1 < value.length) {
        current += value[i + 1];
        i += 1;
        continue;
      }
      if (ch === ',' || ch === '-') {
        if (current.trim()) out.push(current.trim());
        current = '';
        continue;
      }
      current += ch;
    }
    if (current.trim()) out.push(current.trim());
    return out;
  };

  const escapeFilterValue = (value: string) =>
    value
      .replace(/\\/g, '\\\\')
      .replace(/[-,:]/g, '\\$&');

  const normalizeListFilter = (value: string) => splitEscapedList(value).map(escapeFilterValue).join('-');
  const normalizeCategoryFilter = (value: string) =>
    splitEscapedList(value)
      .map((token) => token.trim())
      .map((token) => {
        if (/^\d+$/.test(token)) return token;
        const match = token.match(/(\d{2,})/);
        return match ? match[1] : '';
      })
      .filter(Boolean)
      .join('-');
  const parseCsvValues = (value: string) =>
    value
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  const addMultiValue = (key: keyof FilterValues, value: string) => {
    const nextValue = value.trim();
    if (!nextValue) return;
    setFilters((prev) => {
      const current = parseCsvValues(String(prev[key] ?? ''));
      if (current.includes(nextValue)) return prev;
      return { ...prev, [key]: [...current, nextValue].join(',') };
    });
  };
  const removeMultiValue = (key: keyof FilterValues, value: string) => {
    setFilters((prev) => {
      const current = parseCsvValues(String(prev[key] ?? ''));
      return { ...prev, [key]: current.filter((v) => v !== value).join(',') };
    });
  };
  const normalizeNumberFilter = (value: string) => value.trim();
  const normalizeFoundedFilter = (value: string) => {
    const raw = value.trim();
    if (!raw) return '';
    if (/^\d+$/.test(raw)) return raw;
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '';
    return String(Math.floor(date.getTime() / 1000));
  };

  const params = useMemo(() => {
    const out: Record<string, string> = {};
    if (query) out.query = query;
    if (searchEntity === 'podcasts') out.mode = searchMode;
    if (searchEntity === 'episodes') out.highlight = 'true';
    out.per_page = '50';
    out.page = '1';
    const parts: string[] = [];
    if (filters.listenersMin) parts.push(`listeners:gte:${filters.listenersMin}`);
    if (filters.listenersMax) parts.push(`listeners:lte:${filters.listenersMax}`);
    if (filters.ratingsMin) parts.push(`ratings:gte:${filters.ratingsMin}`);
    if (filters.ratingsMax) parts.push(`ratings:lte:${filters.ratingsMax}`);
    if (filters.socialReachMin) parts.push(`social_reach:gte:${filters.socialReachMin}`);
    if (filters.socialReachMax) parts.push(`social_reach:lte:${filters.socialReachMax}`);
    const categoriesAll = normalizeCategoryFilter(filters.categoriesAll);
    const categoriesAny = normalizeCategoryFilter(filters.categoriesAny);
    if (categoriesAll) parts.push(`categories:in:${categoriesAll}`);
    if (categoriesAny) parts.push(`categories:any:${categoriesAny}`);
    if (filters.locationsAny) parts.push(`locations:any:${normalizeListFilter(filters.locationsAny)}`);
    if (filters.languagesAny) parts.push(`languages:any:${normalizeListFilter(filters.languagesAny)}`);
    if (filters.gendersAny) parts.push(`genders:any:${normalizeListFilter(filters.gendersAny)}`);
    if (filters.active) parts.push(`active:is:${filters.active}`);
    if (filters.sponsored) parts.push(`sponsored:is:${filters.sponsored}`);
    if (filters.sponsorsAny) parts.push(`sponsors:any:${normalizeListFilter(filters.sponsorsAny)}`);
    if (filters.professionsAny) parts.push(`professions:any:${normalizeListFilter(filters.professionsAny)}`);
    if (filters.professionsAll) parts.push(`professions:in:${normalizeListFilter(filters.professionsAll)}`);
    if (filters.interestsAny) parts.push(`interests:any:${normalizeListFilter(filters.interestsAny)}`);
    if (filters.interestsAll) parts.push(`interests:in:${normalizeListFilter(filters.interestsAll)}`);
    if (filters.guests) parts.push(`guests:is:${filters.guests}`);
    if (filters.youtube) parts.push(`youtube:is:${filters.youtube}`);
    if (filters.engagementMin) parts.push(`engagement:gte:${filters.engagementMin}`);
    if (filters.engagementMax) parts.push(`engagement:lte:${filters.engagementMax}`);
    if (filters.frequenciesAny) parts.push(`frequencies:any:${normalizeListFilter(filters.frequenciesAny)}`);
    const episodesMin = normalizeNumberFilter(filters.episodesMin);
    const episodesMax = normalizeNumberFilter(filters.episodesMax);
    const foundedMin = normalizeFoundedFilter(filters.foundedMin);
    const foundedMax = normalizeFoundedFilter(filters.foundedMax);
    if (episodesMin) parts.push(`episodes:gte:${episodesMin}`);
    if (episodesMax) parts.push(`episodes:lte:${episodesMax}`);
    if (foundedMin) parts.push(`founded:gte:${foundedMin}`);
    if (foundedMax) parts.push(`founded:lte:${foundedMax}`);
    if (filters.politicalAny) parts.push(`political_leanings:any:${normalizeListFilter(filters.politicalAny)}`);
    if (filters.ageAny) parts.push(`age_groups:any:${normalizeListFilter(filters.ageAny)}`);
    if (filters.ageAll) parts.push(`age_groups:in:${normalizeListFilter(filters.ageAll)}`);
    if (filters.relationshipAny) parts.push(`relationship_statuses:any:${normalizeListFilter(filters.relationshipAny)}`);
    if (filters.educationAny) parts.push(`education_levels:any:${normalizeListFilter(filters.educationAny)}`);
    if (filters.educationAll) parts.push(`education_levels:in:${normalizeListFilter(filters.educationAll)}`);
    if (filters.incomeMin) parts.push(`household_income:gte:${filters.incomeMin}`);
    if (filters.incomeMax) parts.push(`household_income:lte:${filters.incomeMax}`);
    if (filters.crossPromos) parts.push(`cross_promos:is:${filters.crossPromos}`);
    if (filters.branded) parts.push(`branded:is:${filters.branded}`);
    if (filters.parents) parts.push(`parents:is:${filters.parents}`);
    const filterString = parts.join(',');
    if (filterString) out.filters = filterString;
    return out;
  }, [query, searchEntity, searchMode, filters]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const payload = {
      page: 'podcast_finder',
      updatedAt: new Date().toISOString(),
      rephonic: {
        query: query.trim(),
        entity: searchEntity,
        mode: searchMode,
        params,
        activeFilters: compactPodcastFilters(filters),
        optionCounts: {
          categories: categoryOptions.length,
          countries: countryOptions.length,
          professions: professionOptions.length,
          interests: interestOptions.length,
        },
        counts: {
          podcasts: results.length,
          episodes: episodeResults.length,
          targets: targets.length,
        },
      },
    };
    try {
      const raw = window.localStorage.getItem(GUIDE_CONTEXT_STORAGE_KEY);
      const existing = raw ? JSON.parse(raw) : {};
      const next =
        existing && typeof existing === 'object'
          ? { ...(existing as Record<string, unknown>), podcastFinder: payload, updatedAt: new Date().toISOString() }
          : { podcastFinder: payload, updatedAt: new Date().toISOString() };
      window.localStorage.setItem(GUIDE_CONTEXT_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Ignore non-critical guide context persistence errors.
    }
  }, [
    query,
    searchEntity,
    searchMode,
    params,
    filters,
    categoryOptions.length,
    countryOptions.length,
    professionOptions.length,
    interestOptions.length,
    results.length,
    episodeResults.length,
    targets.length,
  ]);

  const normalizedTargets = useMemo(() => new Map(targets.map((t) => [t.showId, t])), [targets]);

  const extractOptionValues = (payload: any): string[] => {
    const list =
      Array.isArray(payload) ? payload
      : Array.isArray(payload?.results) ? payload.results
      : Array.isArray(payload?.data) ? payload.data
      : Array.isArray(payload?.data?.results) ? payload.data.results
      : Array.isArray(payload?.items) ? payload.items
      : Array.isArray(payload?.categories) ? payload.categories
      : Array.isArray(payload?.countries) ? payload.countries
      : Array.isArray(payload?.professions) ? payload.professions
      : Array.isArray(payload?.interests) ? payload.interests
      : [];
    const out = new Set<string>();
    for (const item of list) {
      if (typeof item === 'string' && item.trim()) out.add(item.trim());
      if (item && typeof item === 'object') {
        const cand = [item.code, item.slug, item.id, item.name, item.label];
        for (const v of cand) {
          if (v != null && String(v).trim()) {
            out.add(String(v).trim());
            break;
          }
        }
      }
    }
    return Array.from(out).sort((a, b) => a.localeCompare(b));
  };

  const extractCategoryOptions = (payload: any): CategoryOption[] => {
    const list =
      Array.isArray(payload) ? payload
      : Array.isArray(payload?.results) ? payload.results
      : Array.isArray(payload?.data) ? payload.data
      : Array.isArray(payload?.data?.results) ? payload.data.results
      : Array.isArray(payload?.items) ? payload.items
      : Array.isArray(payload?.categories) ? payload.categories
      : [];
    const out = new Map<string, string>();
    for (const item of list) {
      if (typeof item === 'number') {
        const code = String(item);
        out.set(code, code);
        continue;
      }
      if (typeof item === 'string') {
        const match = item.match(/(\d{2,})/);
        if (match) out.set(match[1], item.trim() || match[1]);
        continue;
      }
      if (item && typeof item === 'object') {
        const candidates = [item.code, item.id, item.categoryId, item.category_id, item.value];
        const nameCandidates = [item.name, item.label, item.title, item.path, item.full_name, item.fullName];
        const name = nameCandidates
          .map((v) => (v == null ? '' : String(v).trim()))
          .find(Boolean);
        for (const candidate of candidates) {
          if (candidate == null) continue;
          const raw = String(candidate).trim();
          if (/^\d+$/.test(raw)) {
            out.set(raw, name || raw);
            break;
          }
          const match = raw.match(/(\d{2,})/);
          if (match) {
            out.set(match[1], name || raw || match[1]);
            break;
          }
        }
      }
    }
    return Array.from(out.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  const loadTargets = async () => {
    if (!bookingApi || !userEmail) return;
    const res = await fetch(`${bookingApi}/targets/list`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ userEmail, listName: activeListName }),
    });
    const data = await res.json();
    const items = Array.isArray(data?.items) ? data.items : [];
    const nextTargets = items.map((item: any) => {
      const show = item.show?.raw || item.show || {};
      const showId = item.showId || normalizeShowId(show);
      const title = asText(item.show?.title) || asText(show.title) || asText(show.name) || 'Unknown';
      const publisher = showPublisher(item.show) || showPublisher(show);
      return {
        showId,
        title,
        publisher,
        website: showWebsite(item.show) || showWebsite(show),
        rss: showRss(item.show) || showRss(show),
        image: showImageUrl(item.show || show),
        raw: show,
      } as TargetShow;
    });
    setTargets(nextTargets);
    setSelectedTargetIds((prev) => new Set(Array.from(prev).filter((id) => nextTargets.some((t: TargetShow) => t.showId === id))));
  };

  useEffect(() => {
    loadTargets();
  }, [bookingApi, userEmail, activeListName]);

  const persistDefaultList = (listName: string) => {
    setActiveListName(listName);
    localStorage.setItem(DEFAULT_LIST_STORAGE_KEY, listName);
  };

  const loadPodcastLists = async () => {
    if (!userEmail) return;
    const res = await fetch(`${API_CONFIG.RESEARCH_API}/get-lists?userEmail=${encodeURIComponent(userEmail)}&entityType=${LIST_ENTITY_TYPE}`);
    const data = await res.json().catch(() => ({}));
    if (!data?.success) return;
    const nextLists = Array.isArray(data.lists) ? data.lists : [];
    const ensured = nextLists.includes(activeListName) ? nextLists : Array.from(new Set([...nextLists, activeListName]));
    setUserLists(ensured.sort((a: string, b: string) => a.localeCompare(b)));
  };

  useEffect(() => {
    loadPodcastLists();
  }, [userEmail, activeListName]);

  const loadSavedSearches = async () => {
    if (!bookingApi || !userEmail) return;
    const res = await fetch(`${bookingApi}/search/list`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ userEmail }),
    });
    const data = await res.json();
    setSavedSearches(Array.isArray(data?.items) ? data.items : []);
  };

  useEffect(() => {
    loadSavedSearches();
  }, [bookingApi, userEmail]);

  useEffect(() => {
    let cancelled = false;
    const loadCommonFilters = async () => {
      if (!bookingApi) return;
      try {
        const [catsRes, countriesRes, professionsRes, interestsRes] = await Promise.all([
          fetch(`${bookingApi}/common/categories`, { method: 'POST', headers: { 'content-type': 'application/json' } }),
          fetch(`${bookingApi}/common/countries`, { method: 'POST', headers: { 'content-type': 'application/json' } }),
          fetch(`${bookingApi}/common/professions`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ query: 'a' }) }),
          fetch(`${bookingApi}/common/interests`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ query: 'a' }) }),
        ]);
        const payloads = await Promise.allSettled([
          catsRes.ok ? catsRes.json() : Promise.resolve([]),
          countriesRes.ok ? countriesRes.json() : Promise.resolve([]),
          professionsRes.ok ? professionsRes.json() : Promise.resolve([]),
          interestsRes.ok ? interestsRes.json() : Promise.resolve([]),
        ]);
        if (cancelled) return;
        const [cats, countries, professions, interests] = payloads.map((p) => (p.status === 'fulfilled' ? p.value : []));
        setCategoryOptions(extractCategoryOptions(cats));
        setCountryOptions(extractOptionValues(countries));
        setProfessionOptions(extractOptionValues(professions));
        setInterestOptions(extractOptionValues(interests));
      } catch (err) {
        console.error('Failed to load Rephonic common filters', err);
      }
    };
    loadCommonFilters();
    return () => {
      cancelled = true;
    };
  }, [bookingApi]);

  const categoryLabelByCode = useMemo(
    () => new Map(categoryOptions.map((opt) => [opt.value, opt.label])),
    [categoryOptions]
  );
  const RESULTS_PER_PAGE = 10;
  const totalResultsPages = Math.max(
    1,
    Math.ceil((searchEntity === 'episodes' ? episodeResults.length : results.length) / RESULTS_PER_PAGE)
  );
  const pagedResults = useMemo(
    () => results.slice((resultsPage - 1) * RESULTS_PER_PAGE, resultsPage * RESULTS_PER_PAGE),
    [results, resultsPage]
  );
  const pagedEpisodeResults = useMemo(
    () => episodeResults.slice((resultsPage - 1) * RESULTS_PER_PAGE, resultsPage * RESULTS_PER_PAGE),
    [episodeResults, resultsPage]
  );
  const pagePodcastIds = useMemo(() => pagedResults.map((show) => normalizeShowId(show)), [pagedResults]);
  const pageEpisodeIds = useMemo(
    () => pagedEpisodeResults.map((ep, idx) => normalizeEpisodeId(ep, idx)),
    [pagedEpisodeResults]
  );
  const allPodcastsSelected = pagePodcastIds.length > 0 && pagePodcastIds.every((id) => selectedPodcastIds.has(id));
  const allEpisodesSelected = pageEpisodeIds.length > 0 && pageEpisodeIds.every((id) => selectedEpisodeIds.has(id));
  const allTargetsSelected = targets.length > 0 && targets.every((show) => selectedTargetIds.has(show.showId));

  const runSearch = async () => {
    setSearchMessage('');
    if (!bookingApi) {
      setSearchMessage('Booking API is not configured.');
      return;
    }
    if (!params.query && !params.filters) {
      setSearchMessage('Add either a search query or at least one filter.');
      return;
    }
    setLoading(true);
    try {
      const endpoint = searchEntity === 'episodes' ? 'episodes' : 'podcasts';
      const res = await fetch(`${bookingApi}/search/${endpoint}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ params, userEmail }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message = data?.error || data?.message || `Search failed (${res.status})`;
        throw new Error(message);
      }
      setRawResults(data);
      if (searchEntity === 'episodes') {
        const list =
          data?.results ||
          data?.episodes ||
          data?.data?.results ||
          data?.data?.episodes ||
          [];
        setEpisodeResults(Array.isArray(list) ? list : []);
        setResults([]);
        setSelectedPodcastIds(new Set());
      } else {
        const list =
          data?.results ||
          data?.podcasts ||
          data?.data?.results ||
          data?.data?.podcasts ||
          [];
        setResults(Array.isArray(list) ? list : []);
        setEpisodeResults([]);
        setSelectedEpisodeIds(new Set());
      }
      setSelectedPodcastIds(new Set());
      setSelectedEpisodeIds(new Set());
      setResultsPage(1);
      const count = searchEntity === 'episodes' ? (Array.isArray(data?.episodes || data?.results) ? (data?.episodes || data?.results).length : 0) : (Array.isArray(data?.podcasts || data?.results) ? (data?.podcasts || data?.results).length : 0);
      setSearchMessage(`Search completed. ${count} ${searchEntity} loaded.`);
      setActiveSearchId(null);
      await loadSavedSearches();
    } catch (err) {
      console.error(err);
      setSearchMessage(err instanceof Error ? err.message : 'Search failed.');
    } finally {
      setLoading(false);
    }
  };

  const loadSavedSearch = async (searchId: string) => {
    if (!bookingApi || !userEmail) return;
    const res = await fetch(`${bookingApi}/search/get`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ userEmail, searchId }),
    });
    const data = await res.json();
    const podcasts = data?.podcasts ?? [];
    setRawResults(data);
    setResults(Array.isArray(podcasts) ? podcasts : []);
    setResultsPage(1);
    setActiveSearchId(searchId);
  };

  const addTarget = async (show: RephonicShow) => {
    if (!bookingApi || !userEmail) return;
    const showId = normalizeShowId(show);
    const title = asText(show.title) || asText(show.name) || 'Unknown';
    const publisher = showPublisher(show);
    const target: TargetShow = {
      showId,
      title,
      publisher,
      website: showWebsite(show),
      rss: showRss(show),
      image: showImageUrl(show),
      raw: show,
    };
    await fetch(`${bookingApi}/targets/add`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ userEmail, listName: activeListName, show }),
    });
    setTargets((prev) => {
      if (prev.some((p) => p.showId === showId)) return prev;
      return [...prev, target];
    });
    setSelectedPodcastIds((prev) => {
      const next = new Set(prev);
      next.delete(showId);
      return next;
    });
  };

  const addSelectedTargets = async () => {
    if (searchEntity !== 'podcasts' || selectedPodcastIds.size === 0) return;
    const selected = results.filter((show) => selectedPodcastIds.has(normalizeShowId(show)));
    for (const show of selected) {
      await addTarget(show);
    }
    setSelectedPodcastIds(new Set());
  };

  const removeTarget = async (showId: string) => {
    if (!bookingApi || !userEmail) return;
    await fetch(`${bookingApi}/targets/remove`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ userEmail, listName: activeListName, showId }),
    });
    setTargets((prev) => prev.filter((p) => p.showId !== showId));
    setSelectedTargetIds((prev) => {
      const next = new Set(prev);
      next.delete(showId);
      return next;
    });
  };

  const removeSelectedTargets = async () => {
    if (selectedTargetIds.size === 0) return;
    const ids = Array.from(selectedTargetIds);
    for (const id of ids) {
      await removeTarget(id);
    }
    setSelectedTargetIds(new Set());
  };

  const handleCreatePodcastList = async () => {
    if (!userEmail) return;
    const trimmed = newListName.trim();
    if (!trimmed) {
      alert('List name required');
      return;
    }
    const res = await fetch(`${API_CONFIG.RESEARCH_API}/upsert-list`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ userEmail, entityType: LIST_ENTITY_TYPE, listName: trimmed }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.success) throw new Error(data?.error || 'Failed to create podcast list');
    const canonical = String(data.listName || '').trim();
    setUserLists((prev) => Array.from(new Set([...prev, canonical])).sort((a: string, b: string) => a.localeCompare(b)));
    persistDefaultList(canonical);
    setCreateListModalOpen(false);
    setNewListName('');
  };

  const addSelectedPodcastsToHotList = async () => {
    if (!userEmail || selectedPodcastIds.size === 0) return;
    const selected = results.filter((show) => selectedPodcastIds.has(normalizeShowId(show)));
    for (const show of selected) {
      await addPodcastToHotList(show, false);
    }
    alert(`Added ${selected.length} podcasts to Hot List`);
    setSelectedPodcastIds(new Set());
  };

  const addSelectedEpisodesToHotList = async () => {
    if (!userEmail || selectedEpisodeIds.size === 0) return;
    const selected = episodeResults.filter((ep, idx) => selectedEpisodeIds.has(normalizeEpisodeId(ep, idx)));
    for (const ep of selected) {
      await addEpisodeToHotList(ep, false);
    }
    alert(`Added ${selected.length} episodes to Hot List`);
    setSelectedEpisodeIds(new Set());
  };

  const setIntelEntry = (showId: string, source: string, data: any) => {
    setIntel((prev) => ({
      ...prev,
      [showId]: {
        ...(prev[showId] || {}),
        [source]: { data, scrapedAt: new Date().toISOString() },
      },
    }));
  };

  const pollForResults = async (runId: string, datasetId?: string) => {
    for (let i = 0; i < 60; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      const statusRes = await fetch(`${ecomApi}/check_status`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ run_id: runId, dataset_id: datasetId }),
      });
      const status = await statusRes.json();
      const state = status?.status;
      const ds = status?.dataset_id || datasetId;
      if (state === 'SUCCEEDED' || state === 'FAILED' || state === 'ABORTED' || state === 'READY') {
        if (!ds) return [];
        const resultsRes = await fetch(`${ecomApi}/get_results`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ dataset_id: ds }),
        });
        const results = await resultsRes.json();
        return results?.results ?? [];
      }
    }
    return [];
  };

  const runScrape = async (show: TargetShow, source: string, extra?: Record<string, any>) => {
    if (!ecomApi) return;
    const title = asText(show.title) || 'Unknown';
    const body: Record<string, any> = {
      company_name: title,
      website_url: show.website,
      search_query: title,
      contact_name: title,
      userEmail,
      force_refresh: true,
      ...(extra ?? {}),
    };
    enqueueScrapeJob(
      {
        source,
        companyName: title,
        label: `${title} • ${source}`,
      },
      async (update) => {
        update(10, 'Starting scrape...');
        const res = await fetch(`${ecomApi}/scrape/${source}`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (data?.results) {
          setIntelEntry(show.showId, source, data.results);
          setDrawer({ showId: show.showId, title: asText(show.title) || 'Unknown', source, data: data.results });
          update(100, 'Complete');
          return;
        }
        if (data?.dataset_id) {
          update(30, 'Scraping in background...');
          const results = await pollForResults(data.run_id, data.dataset_id);
          setIntelEntry(show.showId, source, results);
          setDrawer({ showId: show.showId, title: asText(show.title) || 'Unknown', source, data: results });
        }
      }
    );
  };

  const fetchEpisodeDetails = async (episodeId?: string) => {
    const id = (episodeId ?? '').trim();
    if (!id || !bookingApi) return false;
    if (episodeDetails[id]?.data && !episodeDetails[id]?.loading) return true;
    setEpisodeDetails((prev) => ({ ...prev, [id]: { loading: true } }));
    try {
      const [detailRes, transcriptRes] = await Promise.all([
        fetch(`${bookingApi}/episodes/get`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ episodeId: id }),
        }),
        fetch(`${bookingApi}/episodes/transcript`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ episodeId: id }),
        }),
      ]);
      const detailData = await detailRes.json().catch(() => ({}));
      const transcriptData = await transcriptRes.json().catch(() => ({}));
      if (!detailRes.ok) {
        throw new Error(detailData?.error || detailData?.message || `Episode details failed (${detailRes.status})`);
      }
      setEpisodeDetails((prev) => ({
        ...prev,
        [id]: {
          loading: false,
          data: detailData,
          transcript: transcriptRes.ok ? transcriptData : undefined,
        },
      }));
      return true;
    } catch (error) {
      setEpisodeDetails((prev) => ({
        ...prev,
        [id]: {
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to load episode details.',
        },
      }));
      return false;
    }
  };

  const loadEpisodeDetails = async (episodeId?: string) => {
    const id = (episodeId ?? '').trim();
    if (!id) return;
    setActiveEpisodeId(id);
    await fetchEpisodeDetails(id);
  };

  const loadEpisodeDetailsInline = async (showId: string, episodeId?: string) => {
    const id = (episodeId ?? '').trim();
    if (!id) return;
    setInlineEpisodeIdByShow((prev) => ({
      ...prev,
      [showId]: prev[showId] === id ? null : id,
    }));
    await fetchEpisodeDetails(id);
  };

  const podcastIdForShow = (show: RephonicShow) => {
    const candidates = [show.id, show.uuid, show.podcast_id, show.podcastId];
    for (const candidate of candidates) {
      if (candidate != null && String(candidate).trim()) return String(candidate).trim();
    }
    return '';
  };

  useEffect(() => {
    if (!bookingApi || searchEntity !== 'podcasts' || pagedResults.length === 0) return;
    let cancelled = false;
    const hydrateArtwork = async () => {
      for (const show of pagedResults) {
        const showId = normalizeShowId(show);
        const current = showImageUrl(show);
        if (current || showArtworkById[showId]) continue;
        const podcastId = podcastIdForShow(show);
        if (!podcastId) continue;
        try {
          const res = await fetch(`${bookingApi}/podcasts/get`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ podcastId, userEmail }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) continue;
          const art = showImageUrl(data as Record<string, any>);
          if (!art || cancelled) continue;
          setShowArtworkById((prev) => (prev[showId] ? prev : { ...prev, [showId]: art }));
        } catch {
          // ignore single-card artwork hydration errors
        }
      }
    };
    hydrateArtwork();
    return () => {
      cancelled = true;
    };
  }, [bookingApi, pagedResults, searchEntity, showArtworkById, userEmail]);

  const loadEpisodesForShow = async (show: RephonicShow, page = 1, queryValue?: string) => {
    if (!bookingApi) return;
    const showId = normalizeShowId(show);
    const podcastId = podcastIdForShow(show);
    if (!podcastId) {
      setShowEpisodes((prev) => ({
        ...prev,
        [showId]: {
          items: [],
          loading: false,
          page: 1,
          more: false,
          query: queryValue ?? prev[showId]?.query ?? '',
          error: 'This show has no podcast_id in search results.',
        },
      }));
      return;
    }
    const activeQuery = (queryValue ?? showEpisodes[showId]?.query ?? '').trim();
    setShowEpisodes((prev) => ({
      ...prev,
      [showId]: {
        items: prev[showId]?.items ?? [],
        loading: true,
        page,
        more: prev[showId]?.more ?? false,
        query: activeQuery,
        error: undefined,
      },
    }));
    try {
      const res = await fetch(`${bookingApi}/episodes/list`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          params: {
            podcast_id: podcastId,
            query: activeQuery || undefined,
            per_page: 25,
            page,
          },
          userEmail,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || data?.message || `Episode fetch failed (${res.status})`);
      }
      const items = Array.isArray(data?.episodes) ? data.episodes : [];
      setShowEpisodes((prev) => ({
        ...prev,
        [showId]: {
          items,
          loading: false,
          page: Number(data?.page || page || 1),
          more: Boolean(data?.more),
          query: activeQuery,
          error: undefined,
        },
      }));
    } catch (error) {
      setShowEpisodes((prev) => ({
        ...prev,
        [showId]: {
          items: prev[showId]?.items ?? [],
          loading: false,
          page,
          more: false,
          query: activeQuery,
          error: error instanceof Error ? error.message : 'Failed to load episodes for this show.',
        },
      }));
    }
  };

  return (
    <div className="booking-agent-page min-h-screen bg-dark-bg text-white">
      <Header />
      <div className="booking-agent-hero relative overflow-hidden border-b border-gray-800/60 bg-gradient-to-br from-[#0f1119] via-[#0a0b10] to-[#111827]">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="flex flex-col gap-3">
            <p className="text-xs uppercase tracking-[0.3em] text-indigo-300/70">BookingAIgent</p>
            <h1 className="text-3xl font-semibold">Branded Podcast Discovery</h1>
            <p className="text-sm text-gray-300 max-w-2xl">
              White-labeled Rephonic search with owned lists, enrichment, and social intelligence.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-6">
        <div className="space-y-6">
          <div className="bg-[#11131a] border border-gray-800/70 rounded-2xl p-5 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-semibold">Search</p>
              <button
                className="text-xs px-3 py-1 rounded bg-indigo-600 disabled:bg-indigo-900/40"
                onClick={runSearch}
                disabled={loading}
              >
                {loading ? 'Searching…' : 'Search'}
              </button>
            </div>
            <input
              className="w-full bg-[#0b0d12] border border-gray-700 rounded-lg px-3 py-2 text-sm"
              placeholder={searchEntity === 'episodes' ? 'Search episodes...' : 'Search podcasts...'}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="mt-2">
              <label className="block text-[11px] text-gray-400 mb-1">Search type</label>
              <select
                value={searchEntity}
                onChange={(e) => setSearchEntity(e.target.value as 'podcasts' | 'episodes')}
                className="w-full bg-[#0b0d12] border border-gray-700 rounded-lg px-3 py-2 text-sm"
              >
                <option value="podcasts">Podcasts</option>
                <option value="episodes">Episodes</option>
              </select>
            </div>
            {searchEntity === 'podcasts' && (
              <div className="mt-2">
                <label className="block text-[11px] text-gray-400 mb-1">Search mode</label>
                <select
                  value={searchMode}
                  onChange={(e) => setSearchMode(e.target.value as 'topics' | 'titles' | 'publishers')}
                  className="w-full bg-[#0b0d12] border border-gray-700 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="topics">Topics</option>
                  <option value="titles">Titles</option>
                  <option value="publishers">Publishers</option>
                </select>
              </div>
            )}
            <p className="text-[11px] text-gray-500 mt-2">
              Rephonic filters format: <span className="text-gray-300">listeners:gte:5000,active:is:true</span>
            </p>
            <p className="text-[11px] text-gray-500 mt-1">
              Query is optional if you set at least one filter.
            </p>
            {searchMessage && (
              <p className="text-[11px] mt-2 text-amber-300">{searchMessage}</p>
            )}
          </div>

          <datalist id="rephonic-categories">
            {categoryOptions.map((opt) => (
              <option key={`cat-${opt.value}`} value={opt.value} />
            ))}
          </datalist>
          <datalist id="rephonic-countries">
            {countryOptions.map((opt) => (
              <option key={`country-${opt}`} value={opt} />
            ))}
          </datalist>
          <datalist id="rephonic-professions">
            {professionOptions.map((opt) => (
              <option key={`prof-${opt}`} value={opt} />
            ))}
          </datalist>
          <datalist id="rephonic-interests">
            {interestOptions.map((opt) => (
              <option key={`interest-${opt}`} value={opt} />
            ))}
          </datalist>
          <datalist id="rephonic-frequencies">
            {FREQUENCY_VALUES.map((opt) => (
              <option key={`freq-${opt}`} value={opt} />
            ))}
          </datalist>
          <datalist id="rephonic-political">
            {POLITICAL_VALUES.map((opt) => (
              <option key={`pol-${opt}`} value={opt} />
            ))}
          </datalist>
          <datalist id="rephonic-age-groups">
            {AGE_GROUP_VALUES.map((opt) => (
              <option key={`age-${opt}`} value={opt} />
            ))}
          </datalist>
          <datalist id="rephonic-relationship">
            {RELATIONSHIP_VALUES.map((opt) => (
              <option key={`rel-${opt}`} value={opt} />
            ))}
          </datalist>
          <datalist id="rephonic-education">
            {EDUCATION_VALUES.map((opt) => (
              <option key={`edu-${opt}`} value={opt} />
            ))}
          </datalist>

          <div className="bg-[#11131a] border border-gray-800/70 rounded-2xl p-5 shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold">Filters</p>
              <button className="text-xs px-3 py-1 rounded bg-gray-800" onClick={() => setFilters({ ...EMPTY_FILTERS })}>
                Clear all
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Listeners (min)</span>
                  <input className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.listenersMin} onChange={(e) => setFilters({ ...filters, listenersMin: e.target.value })} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Listeners (max)</span>
                  <input className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.listenersMax} onChange={(e) => setFilters({ ...filters, listenersMax: e.target.value })} />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Ratings (min)</span>
                  <input className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.ratingsMin} onChange={(e) => setFilters({ ...filters, ratingsMin: e.target.value })} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Ratings (max)</span>
                  <input className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.ratingsMax} onChange={(e) => setFilters({ ...filters, ratingsMax: e.target.value })} />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Social reach (min)</span>
                  <input className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.socialReachMin} onChange={(e) => setFilters({ ...filters, socialReachMin: e.target.value })} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Social reach (max)</span>
                  <input className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.socialReachMax} onChange={(e) => setFilters({ ...filters, socialReachMax: e.target.value })} />
                </label>
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-gray-400">Categories (must include all)</span>
                <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value="" onChange={(e) => addMultiValue('categoriesAll', e.target.value)}>
                  <option value="">Select category...</option>
                  {categoryOptions.map((opt) => (
                    <option key={`cat-all-${opt.value}`} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <div className="flex flex-wrap gap-1 mt-1">
                  {parseCsvValues(filters.categoriesAll).map((v) => (
                    <button key={`cat-all-chip-${v}`} type="button" className="text-[11px] px-2 py-0.5 rounded bg-indigo-500/20 border border-indigo-400/40" onClick={() => removeMultiValue('categoriesAll', v)}>
                      {(categoryLabelByCode.get(v) ?? v)} ×
                    </button>
                  ))}
                </div>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-gray-400">Categories (any)</span>
                <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value="" onChange={(e) => addMultiValue('categoriesAny', e.target.value)}>
                  <option value="">Select category...</option>
                  {categoryOptions.map((opt) => (
                    <option key={`cat-any-${opt.value}`} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <div className="flex flex-wrap gap-1 mt-1">
                  {parseCsvValues(filters.categoriesAny).map((v) => (
                    <button key={`cat-any-chip-${v}`} type="button" className="text-[11px] px-2 py-0.5 rounded bg-indigo-500/20 border border-indigo-400/40" onClick={() => removeMultiValue('categoriesAny', v)}>
                      {(categoryLabelByCode.get(v) ?? v)} ×
                    </button>
                  ))}
                </div>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-gray-400">Audience locations</span>
                <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value="" onChange={(e) => addMultiValue('locationsAny', e.target.value)}>
                  <option value="">Select country...</option>
                  {countryOptions.map((opt) => (
                    <option key={`country-any-${opt}`} value={opt}>{opt}</option>
                  ))}
                </select>
                <div className="flex flex-wrap gap-1 mt-1">
                  {parseCsvValues(filters.locationsAny).map((v) => (
                    <button key={`location-any-chip-${v}`} type="button" className="text-[11px] px-2 py-0.5 rounded bg-indigo-500/20 border border-indigo-400/40" onClick={() => removeMultiValue('locationsAny', v)}>
                      {v} ×
                    </button>
                  ))}
                </div>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-gray-400">Languages</span>
                <input className="bg-black/40 border border-gray-700 rounded px-2 py-1" placeholder="en-es-fr" value={filters.languagesAny} onChange={(e) => setFilters({ ...filters, languagesAny: e.target.value })} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-gray-400">Gender skew</span>
                <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.gendersAny} onChange={(e) => setFilters({ ...filters, gendersAny: e.target.value })}>
                  <option value="">Any</option>
                  <option value="verymale">Very male</option>
                  <option value="male">Male</option>
                  <option value="neutral">Neutral</option>
                  <option value="female">Female</option>
                  <option value="veryfemale">Very female</option>
                </select>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Active (last 60 days)</span>
                  <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.active} onChange={(e) => setFilters({ ...filters, active: e.target.value })}>
                    <option value="">Any</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Sponsored</span>
                  <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.sponsored} onChange={(e) => setFilters({ ...filters, sponsored: e.target.value })}>
                    <option value="">Any</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </label>
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-gray-400">Sponsors (any)</span>
                <input className="bg-black/40 border border-gray-700 rounded px-2 py-1" placeholder="IBM-Apple-Microsoft" value={filters.sponsorsAny} onChange={(e) => setFilters({ ...filters, sponsorsAny: e.target.value })} />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Professions (any)</span>
                  <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value="" onChange={(e) => addMultiValue('professionsAny', e.target.value)}>
                    <option value="">Select profession...</option>
                    {professionOptions.map((opt) => (
                      <option key={`prof-any-${opt}`} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {parseCsvValues(filters.professionsAny).map((v) => (
                      <button key={`prof-any-chip-${v}`} type="button" className="text-[11px] px-2 py-0.5 rounded bg-indigo-500/20 border border-indigo-400/40" onClick={() => removeMultiValue('professionsAny', v)}>
                        {v} ×
                      </button>
                    ))}
                  </div>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Professions (all)</span>
                  <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value="" onChange={(e) => addMultiValue('professionsAll', e.target.value)}>
                    <option value="">Select profession...</option>
                    {professionOptions.map((opt) => (
                      <option key={`prof-all-${opt}`} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {parseCsvValues(filters.professionsAll).map((v) => (
                      <button key={`prof-all-chip-${v}`} type="button" className="text-[11px] px-2 py-0.5 rounded bg-indigo-500/20 border border-indigo-400/40" onClick={() => removeMultiValue('professionsAll', v)}>
                        {v} ×
                      </button>
                    ))}
                  </div>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Interests (any)</span>
                  <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value="" onChange={(e) => addMultiValue('interestsAny', e.target.value)}>
                    <option value="">Select interest...</option>
                    {interestOptions.map((opt) => (
                      <option key={`interest-any-${opt}`} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {parseCsvValues(filters.interestsAny).map((v) => (
                      <button key={`interest-any-chip-${v}`} type="button" className="text-[11px] px-2 py-0.5 rounded bg-indigo-500/20 border border-indigo-400/40" onClick={() => removeMultiValue('interestsAny', v)}>
                        {v} ×
                      </button>
                    ))}
                  </div>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Interests (all)</span>
                  <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value="" onChange={(e) => addMultiValue('interestsAll', e.target.value)}>
                    <option value="">Select interest...</option>
                    {interestOptions.map((opt) => (
                      <option key={`interest-all-${opt}`} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {parseCsvValues(filters.interestsAll).map((v) => (
                      <button key={`interest-all-chip-${v}`} type="button" className="text-[11px] px-2 py-0.5 rounded bg-indigo-500/20 border border-indigo-400/40" onClick={() => removeMultiValue('interestsAll', v)}>
                        {v} ×
                      </button>
                    ))}
                  </div>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Guests</span>
                  <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.guests} onChange={(e) => setFilters({ ...filters, guests: e.target.value })}>
                    <option value="">Any</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">YouTube channel</span>
                  <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.youtube} onChange={(e) => setFilters({ ...filters, youtube: e.target.value })}>
                    <option value="">Any</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Engagement (min)</span>
                  <input className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.engagementMin} onChange={(e) => setFilters({ ...filters, engagementMin: e.target.value })} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Engagement (max)</span>
                  <input className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.engagementMax} onChange={(e) => setFilters({ ...filters, engagementMax: e.target.value })} />
                </label>
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-gray-400">Publishing frequency (any)</span>
                <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.frequenciesAny} onChange={(e) => setFilters({ ...filters, frequenciesAny: e.target.value })}>
                  <option value="">Any</option>
                  {FREQUENCY_VALUES.map((v) => (
                    <option key={`freq-opt-${v}`} value={v}>{v}</option>
                  ))}
                </select>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Episodes (min)</span>
                  <input className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.episodesMin} onChange={(e) => setFilters({ ...filters, episodesMin: e.target.value })} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Episodes (max)</span>
                  <input className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.episodesMax} onChange={(e) => setFilters({ ...filters, episodesMax: e.target.value })} />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Founded after (unix)</span>
                  <input className="bg-black/40 border border-gray-700 rounded px-2 py-1" placeholder="2018-01-30 or 1517270400" value={filters.foundedMin} onChange={(e) => setFilters({ ...filters, foundedMin: e.target.value })} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Founded before (unix)</span>
                  <input className="bg-black/40 border border-gray-700 rounded px-2 py-1" placeholder="2020-05-20 or 1589932800" value={filters.foundedMax} onChange={(e) => setFilters({ ...filters, foundedMax: e.target.value })} />
                </label>
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-gray-400">Political leaning</span>
                <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.politicalAny} onChange={(e) => setFilters({ ...filters, politicalAny: e.target.value })}>
                  <option value="">Any</option>
                  {POLITICAL_VALUES.map((v) => (
                    <option key={`pol-opt-${v}`} value={v}>{v}</option>
                  ))}
                </select>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Age groups (any)</span>
                  <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.ageAny} onChange={(e) => setFilters({ ...filters, ageAny: e.target.value })}>
                    <option value="">Any</option>
                    {AGE_GROUP_VALUES.map((v) => (
                      <option key={`age-any-opt-${v}`} value={v}>{v}</option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Age groups (all)</span>
                  <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.ageAll} onChange={(e) => setFilters({ ...filters, ageAll: e.target.value })}>
                    <option value="">Any</option>
                    {AGE_GROUP_VALUES.map((v) => (
                      <option key={`age-all-opt-${v}`} value={v}>{v}</option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-gray-400">Relationship status</span>
                <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.relationshipAny} onChange={(e) => setFilters({ ...filters, relationshipAny: e.target.value })}>
                  <option value="">Any</option>
                  {RELATIONSHIP_VALUES.map((v) => (
                    <option key={`rel-opt-${v}`} value={v}>{v}</option>
                  ))}
                </select>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Education (any)</span>
                  <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.educationAny} onChange={(e) => setFilters({ ...filters, educationAny: e.target.value })}>
                    <option value="">Any</option>
                    {EDUCATION_VALUES.map((v) => (
                      <option key={`edu-any-opt-${v}`} value={v}>{v}</option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Education (all)</span>
                  <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.educationAll} onChange={(e) => setFilters({ ...filters, educationAll: e.target.value })}>
                    <option value="">Any</option>
                    {EDUCATION_VALUES.map((v) => (
                      <option key={`edu-all-opt-${v}`} value={v}>{v}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Household income (min)</span>
                  <input className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.incomeMin} onChange={(e) => setFilters({ ...filters, incomeMin: e.target.value })} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Household income (max)</span>
                  <input className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.incomeMax} onChange={(e) => setFilters({ ...filters, incomeMax: e.target.value })} />
                </label>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Cross promos</span>
                  <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.crossPromos} onChange={(e) => setFilters({ ...filters, crossPromos: e.target.value })}>
                    <option value="">Any</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Branded</span>
                  <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.branded} onChange={(e) => setFilters({ ...filters, branded: e.target.value })}>
                    <option value="">Any</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-gray-400">Parents</span>
                  <select className="bg-black/40 border border-gray-700 rounded px-2 py-1" value={filters.parents} onChange={(e) => setFilters({ ...filters, parents: e.target.value })}>
                    <option value="">Any</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </label>
              </div>
            </div>
          </div>

          <div className="bg-[#11131a] border border-gray-800/70 rounded-2xl p-5 shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold">Saved Searches</p>
              <button className="text-xs px-3 py-1 rounded bg-gray-800" onClick={loadSavedSearches}>
                Refresh
              </button>
            </div>
            {savedSearches.length === 0 ? (
              <p className="text-sm text-gray-400">No saved searches yet.</p>
            ) : (
              <div className="space-y-2 max-h-56 overflow-auto">
                {savedSearches.map((item) => {
                  const isActive = activeSearchId === item.searchId;
                  return (
                    <button
                      key={item.searchId}
                      className={`w-full text-left text-xs px-3 py-2 rounded border ${
                        isActive ? 'border-emerald-500/60 bg-emerald-500/10' : 'border-gray-700/60 bg-black/40'
                      }`}
                      onClick={() => loadSavedSearch(item.searchId)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-gray-200">{item.query || 'Search'}</span>
                        <span className="text-gray-500">{formatDate(item.createdAt)}</span>
                      </div>
                      <div className="text-gray-500 mt-1">
                        {item.filters ? `filters: ${item.filters}` : 'no filters'} · {formatNumber(item.resultCount)} results
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-[#11131a] border border-gray-800/70 rounded-2xl p-5 shadow-lg">
            <p className="text-sm font-semibold mb-2">Raw Response</p>
            <pre className="text-xs text-gray-400 max-h-64 overflow-auto">
              {rawResults ? JSON.stringify(rawResults, null, 2) : 'No results yet.'}
            </pre>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-[#11131a] border border-gray-800/70 rounded-2xl p-5 shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold">
                Results ({searchEntity === 'episodes' ? episodeResults.length : results.length})
              </p>
              {(searchEntity === 'episodes' ? episodeResults.length > 0 : results.length > 0) && (
                <div className="flex items-center gap-2 text-xs">
                  {searchEntity === 'podcasts' ? (
                    <>
                      <label className="inline-flex items-center gap-1 text-gray-300">
                        <input
                          type="checkbox"
                          checked={allPodcastsSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPodcastIds((prev) => new Set([...Array.from(prev), ...pagePodcastIds]));
                            } else {
                              setSelectedPodcastIds((prev) => {
                                const next = new Set(prev);
                                pagePodcastIds.forEach((id) => next.delete(id));
                                return next;
                              });
                            }
                          }}
                        />
                        Select page
                      </label>
                      <button
                        className="px-2 py-1 rounded bg-emerald-700 disabled:opacity-40"
                        disabled={selectedPodcastIds.size === 0}
                        onClick={() => addSelectedTargets()}
                      >
                        Add Selected ({selectedPodcastIds.size})
                      </button>
                      <button
                        className="px-2 py-1 rounded bg-orange-700 disabled:opacity-40"
                        disabled={selectedPodcastIds.size === 0}
                        onClick={() => addSelectedPodcastsToHotList()}
                      >
                        Hot List Selected
                      </button>
                    </>
                  ) : (
                    <>
                      <label className="inline-flex items-center gap-1 text-gray-300">
                        <input
                          type="checkbox"
                          checked={allEpisodesSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEpisodeIds((prev) => new Set([...Array.from(prev), ...pageEpisodeIds]));
                            } else {
                              setSelectedEpisodeIds((prev) => {
                                const next = new Set(prev);
                                pageEpisodeIds.forEach((id) => next.delete(id));
                                return next;
                              });
                            }
                          }}
                        />
                        Select page
                      </label>
                      <button
                        className="px-2 py-1 rounded bg-orange-700 disabled:opacity-40"
                        disabled={selectedEpisodeIds.size === 0}
                        onClick={() => addSelectedEpisodesToHotList()}
                      >
                        Hot List Selected
                      </button>
                    </>
                  )}
                  <button
                    className="px-2 py-1 rounded bg-gray-800 disabled:opacity-40"
                    disabled={resultsPage <= 1}
                    onClick={() => setResultsPage((p) => Math.max(1, p - 1))}
                  >
                    Prev
                  </button>
                  <span className="text-gray-400">
                    Page {resultsPage} / {totalResultsPages}
                  </span>
                  <button
                    className="px-2 py-1 rounded bg-gray-800 disabled:opacity-40"
                    disabled={resultsPage >= totalResultsPages}
                    onClick={() => setResultsPage((p) => Math.min(totalResultsPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
            {searchEntity === 'episodes' ? (
              pagedEpisodeResults.length === 0 ? (
                <p className="text-sm text-gray-400">No episode results yet.</p>
              ) : (
                <div className="space-y-3">
                  {pagedEpisodeResults.map((ep, idx) => (
                    <div key={`${ep.id || ep.title || 'ep'}-${idx}`} className="bg-gray-800/60 rounded-xl p-4">
                      <div className="mb-2">
                        <label className="inline-flex items-center gap-2 text-xs text-gray-300">
                          <input
                            type="checkbox"
                            checked={selectedEpisodeIds.has(normalizeEpisodeId(ep, idx))}
                            onChange={(e) => {
                              const epId = normalizeEpisodeId(ep, idx);
                              setSelectedEpisodeIds((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(epId);
                                else next.delete(epId);
                                return next;
                              });
                            }}
                          />
                          Select
                        </label>
                      </div>
                      <div className="flex justify-end mb-2">
                        <button
                          onClick={() => addEpisodeToHotList(ep)}
                          className="text-xs px-3 py-1 rounded bg-orange-700 hover:bg-orange-600"
                        >
                          Hot List
                        </button>
                      </div>
                      <EpisodeInsights source={ep} onViewDetails={loadEpisodeDetails} />
                    </div>
                  ))}
                </div>
              )
            ) : results.length === 0 ? (
              <p className="text-sm text-gray-400">No results yet.</p>
            ) : (
              <div className="space-y-3">
                {pagedResults.map((show, idx) => {
                  const showId = normalizeShowId(show);
                  const showCardImage = showImageUrl(show) || showArtworkById[showId] || '';
                  const isAdded = normalizedTargets.has(showId);
                  const isExpanded = expandedId === showId;
                  const title = asText(show.title) || asText(show.name) || 'Unknown';
                  const publisher = showPublisher(show) || 'Unknown';
                  const downloadsPerEpisode = pickMetric(show, ['downloads_per_episode', 'downloadsPerEpisode']);
                  const weeklyDownloads = pickMetric(show, ['est_weekly_downloads', 'estWeeklyDownloads', 'weekly_downloads']);
                  const socialReach = pickMetric(show, ['social_reach', 'socialReach']);
                  const engagement = pickMetric(show, ['engagement']);
                  const sponsored = show.sponsored === true;
                  const branded = show.branded === true;
                  const active = show.active === true;
                  const acceptsGuests = show.guests?.accepts_guests === true;
                  return (
                    <div key={`${showId}-${idx}`} className="bg-gray-800/60 rounded-xl p-4">
                      <div
                        className="w-full flex items-center gap-4 text-left cursor-pointer"
                        onClick={() => setExpandedId(isExpanded ? null : showId)}
                      >
                        <label
                          className="inline-flex items-center justify-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={selectedPodcastIds.has(showId)}
                            onChange={(e) => {
                              setSelectedPodcastIds((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(showId);
                                else next.delete(showId);
                                return next;
                              });
                            }}
                          />
                        </label>
                        <div className="w-12 h-12 rounded-lg bg-gray-700 overflow-hidden">
                          {showCardImage && (
                            <img
                              src={showCardImage}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="text-sm font-semibold">{title}</div>
                            {active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200">Active</span>}
                            {sponsored && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-200">Sponsored</span>}
                            {acceptsGuests && <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-200">Guests</span>}
                            {branded && <span className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-500/20 text-fuchsia-200">Branded</span>}
                          </div>
                          <div className="text-xs text-gray-400">{publisher}</div>
                          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] text-gray-300">
                            <div>Downloads/ep: <span className="text-white">{formatNumber(downloadsPerEpisode)}</span></div>
                            <div>Weekly DLs: <span className="text-white">{formatNumber(weeklyDownloads)}</span></div>
                            <div>Social reach: <span className="text-white">{formatNumber(socialReach)}</span></div>
                            <div>Engagement: <span className="text-white">{formatNumber(engagement)}</span></div>
                          </div>
                          <div className="text-xs text-gray-500 line-clamp-2 mt-2">
                            {stripHtml(show.summary || show.description)}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            addTarget(show);
                          }}
                          disabled={isAdded}
                          className="text-xs px-3 py-1 rounded bg-emerald-600 disabled:bg-gray-700"
                        >
                          {isAdded ? 'Added' : 'Add'}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            addPodcastToHotList(show);
                          }}
                          className="text-xs px-3 py-1 rounded bg-orange-700 hover:bg-orange-600"
                        >
                          Hot List
                        </button>
                      </div>
                      {isExpanded && (
                        <div className="mt-4 border-t border-gray-700/60 pt-4 space-y-4 text-xs text-gray-300">
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            <div className="space-y-2">
                            <div className="text-[11px] uppercase tracking-wider text-gray-500">Episode Breakdown</div>
                            <div>Frequency: <span className="text-white">{show.frequency || '—'}</span></div>
                            <div>Episodes: <span className="text-white">{formatNumber(show.num_episodes ?? show.numEpisodes)}</span></div>
                            <div>Founded: <span className="text-white">{formatDate(show.founded)}</span></div>
                            <div>Ratings: <span className="text-white">{formatNumber(show.num_ratings ?? show.numRatings)}</span></div>
                          </div>
                          <div className="space-y-2">
                            <div className="text-[11px] uppercase tracking-wider text-gray-500">Links</div>
                            <div className="break-all">Website: <span className="text-white">{show.web_url || show.website || show.site || '—'}</span></div>
                            <div className="break-all">Feed: <span className="text-white">{show.feed_url || show.rss || show.rssUrl || '—'}</span></div>
                            <div>Email: <span className="text-white">{show.email || '—'}</span></div>
                            <div>Genres: <span className="text-white">{(show.genres || []).join(', ') || '—'}</span></div>
                          </div>
                          <div className="space-y-2">
                            <div className="text-[11px] uppercase tracking-wider text-gray-500">Ad Cost (Est.)</div>
                            <div>Min CPM: <span className="text-white">{formatNumber(show.estimated_ad_cost?.min_cpm)}</span></div>
                            <div>Max CPM: <span className="text-white">{formatNumber(show.estimated_ad_cost?.max_cpm)}</span></div>
                            <div>Min Cost: <span className="text-white">{formatNumber(show.estimated_ad_cost?.min_cost)}</span></div>
                            <div>Max Cost: <span className="text-white">{formatNumber(show.estimated_ad_cost?.max_cost)}</span></div>
                          </div>
                          </div>
                          <div className="bg-black/30 border border-gray-700/60 rounded-lg p-3">
                            <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Description</div>
                            <div className="text-gray-200 whitespace-pre-wrap">
                              {stripHtml(show.summary || show.description) || '—'}
                            </div>
                          </div>
                          <div className="bg-black/30 border border-gray-700/60 rounded-lg p-3 space-y-3">
                            <div className="flex flex-wrap items-center gap-2">
                              <div className="text-[11px] uppercase tracking-wider text-gray-500">Show Episodes</div>
                              <input
                                value={showEpisodes[showId]?.query ?? ''}
                                onChange={(e) =>
                                  setShowEpisodes((prev) => ({
                                    ...prev,
                                    [showId]: {
                                      items: prev[showId]?.items ?? [],
                                      loading: false,
                                      error: undefined,
                                      page: 1,
                                      more: false,
                                      query: e.target.value,
                                    },
                                  }))
                                }
                                placeholder="Filter episodes in this show"
                                className="bg-black/40 border border-gray-700 rounded px-2 py-1 text-xs min-w-[220px]"
                              />
                              <button
                                className="px-2 py-1 rounded bg-sky-700 text-[11px] disabled:bg-gray-700"
                                onClick={() => loadEpisodesForShow(show, 1)}
                                disabled={Boolean(showEpisodes[showId]?.loading)}
                              >
                                {showEpisodes[showId]?.loading ? 'Loading…' : 'Find Episodes'}
                              </button>
                            </div>
                            {showEpisodes[showId]?.error && (
                              <div className="text-rose-300">{showEpisodes[showId]?.error}</div>
                            )}
                            {!showEpisodes[showId]?.loading && (showEpisodes[showId]?.items?.length ?? 0) === 0 ? (
                              <div className="text-gray-500">No episodes loaded yet for this show.</div>
                            ) : null}
                            {(showEpisodes[showId]?.items?.length ?? 0) > 0 && (
                              <div className="space-y-3">
                                {showEpisodes[showId].items.map((ep, epIdx) => (
                                  <div key={`${ep.id || ep.title || 'show-ep'}-${epIdx}`} className="border border-gray-700/50 rounded-lg p-3 bg-gray-900/40">
                                    <EpisodeInsights
                                      source={ep}
                                      onViewDetails={(episodeId) => loadEpisodeDetailsInline(showId, episodeId)}
                                    />
                                    {inlineEpisodeIdByShow[showId] && inlineEpisodeIdByShow[showId] === String(ep.id || '') && (
                                      <div className="mt-3 border-t border-gray-700/60 pt-3">
                                        {episodeDetails[String(ep.id || '')]?.loading ? (
                                          <div className="text-sm text-gray-400">Loading episode details…</div>
                                        ) : episodeDetails[String(ep.id || '')]?.error ? (
                                          <div className="text-sm text-rose-300">{episodeDetails[String(ep.id || '')]?.error}</div>
                                        ) : (
                                          <EpisodeInsights
                                            source={episodeDetails[String(ep.id || '')]?.data ?? {}}
                                            transcript={episodeDetails[String(ep.id || '')]?.transcript ?? {}}
                                          />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                                <div className="flex items-center gap-2">
                                  <button
                                    className="px-2 py-1 rounded bg-gray-800 disabled:opacity-40"
                                    disabled={showEpisodes[showId].loading || (showEpisodes[showId].page ?? 1) <= 1}
                                    onClick={() =>
                                      loadEpisodesForShow(show, Math.max(1, (showEpisodes[showId].page ?? 1) - 1))
                                    }
                                  >
                                    Prev
                                  </button>
                                  <span className="text-gray-400">Page {showEpisodes[showId].page || 1}</span>
                                  <button
                                    className="px-2 py-1 rounded bg-gray-800 disabled:opacity-40"
                                    disabled={showEpisodes[showId].loading || !showEpisodes[showId].more}
                                    onClick={() => loadEpisodesForShow(show, (showEpisodes[showId].page ?? 1) + 1)}
                                  >
                                    Next
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold">Target Shows</p>
                <select
                  value={activeListName}
                  onChange={(e) => persistDefaultList(e.target.value)}
                  className="px-2 py-1 rounded bg-gray-800 text-xs text-white border border-gray-700 min-w-[220px]"
                >
                  {userLists.map((list) => (
                    <option key={list} value={list}>
                      {list}
                    </option>
                  ))}
                </select>
                <button
                  className="px-2 py-1 rounded bg-gray-800 text-xs"
                  onClick={() => setCreateListModalOpen(true)}
                >
                  Create List
                </button>
              </div>
              {targets.length > 0 && (
                <div className="flex items-center gap-2 text-xs">
                  <label className="inline-flex items-center gap-1 text-gray-300">
                    <input
                      type="checkbox"
                      checked={allTargetsSelected}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTargetIds(new Set(targets.map((show) => show.showId)));
                        } else {
                          setSelectedTargetIds(new Set());
                        }
                      }}
                    />
                    Select all
                  </label>
                  <button
                    className="px-2 py-1 rounded bg-red-800 disabled:opacity-40"
                    disabled={selectedTargetIds.size === 0}
                    onClick={() => removeSelectedTargets()}
                  >
                    Remove Selected ({selectedTargetIds.size})
                  </button>
                </div>
              )}
            </div>
            {targets.length === 0 ? (
              <p className="text-sm text-gray-400">No targets yet.</p>
            ) : (
              <div className="space-y-4">
                {targets.map((show) => {
                  const showIntel = intel[show.showId] || {};
                  return (
                    <div key={show.showId} className="bg-gray-800/60 rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-2">
                          <label className="inline-flex items-center mt-1">
                            <input
                              type="checkbox"
                              checked={selectedTargetIds.has(show.showId)}
                              onChange={(e) => {
                                setSelectedTargetIds((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(show.showId);
                                  else next.delete(show.showId);
                                  return next;
                                });
                              }}
                            />
                          </label>
                          <div>
                            <div className="text-sm font-semibold">{asText(show.title) || 'Unknown'}</div>
                            <div className="text-xs text-gray-400">{showPublisher(show) || 'Unknown'}</div>
                          </div>
                        </div>
                        <button className="text-xs text-gray-400" onClick={() => removeTarget(show.showId)}>
                          Remove
                        </button>
                      </div>

                      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <div>
                          <div className="text-gray-400 mb-1">Podcast</div>
                          <ScrapeButton
                            hasData={Boolean(showIntel.podcast?.data)}
                            creditCost={CREDIT_COSTS.podcast ?? 2}
                            onScrape={() => runScrape(show, 'podcast')}
                            onRescrape={() => runScrape(show, 'podcast')}
                            onViewData={() => setDrawer({ showId: show.showId, title: asText(show.title) || 'Unknown', source: 'podcast', data: showIntel.podcast?.data })}
                          />
                        </div>
                        <div>
                          <div className="text-gray-400 mb-1">Website</div>
                          <ScrapeButton
                            hasData={Boolean(showIntel.website?.data)}
                            creditCost={CREDIT_COSTS.website ?? 5}
                            onScrape={() => runScrape(show, 'website')}
                            onRescrape={() => runScrape(show, 'website')}
                            onViewData={() => setDrawer({ showId: show.showId, title: asText(show.title) || 'Unknown', source: 'website', data: showIntel.website?.data })}
                          />
                        </div>
                        <div>
                          <div className="text-gray-400 mb-1">YouTube</div>
                          <ScrapeButton
                            hasData={Boolean(showIntel.youtube?.data)}
                            creditCost={CREDIT_COSTS.youtube ?? 2}
                            onScrape={() => runScrape(show, 'youtube')}
                            onRescrape={() => runScrape(show, 'youtube')}
                            onViewData={() => setDrawer({ showId: show.showId, title: asText(show.title) || 'Unknown', source: 'youtube', data: showIntel.youtube?.data })}
                          />
                        </div>
                        <div>
                          <div className="text-gray-400 mb-1">X</div>
                          <ScrapeButton
                            hasData={Boolean(showIntel.x?.data)}
                            creditCost={CREDIT_COSTS.x ?? 2}
                            onScrape={() => runScrape(show, 'x')}
                            onRescrape={() => runScrape(show, 'x')}
                            onViewData={() => setDrawer({ showId: show.showId, title: asText(show.title) || 'Unknown', source: 'x', data: showIntel.x?.data })}
                          />
                        </div>
                        <div>
                          <div className="text-gray-400 mb-1">Instagram</div>
                          <ScrapeButton
                            hasData={Boolean(showIntel.instagram?.data)}
                            creditCost={CREDIT_COSTS.instagram ?? 2}
                            onScrape={() => runScrape(show, 'instagram')}
                            onRescrape={() => runScrape(show, 'instagram')}
                            onViewData={() => setDrawer({ showId: show.showId, title: asText(show.title) || 'Unknown', source: 'instagram', data: showIntel.instagram?.data })}
                          />
                        </div>
                        <div>
                          <div className="text-gray-400 mb-1">Facebook</div>
                          <ScrapeButton
                            hasData={Boolean(showIntel.facebook?.data)}
                            creditCost={CREDIT_COSTS.facebook ?? 2}
                            onScrape={() => runScrape(show, 'facebook')}
                            onRescrape={() => runScrape(show, 'facebook')}
                            onViewData={() => setDrawer({ showId: show.showId, title: asText(show.title) || 'Unknown', source: 'facebook', data: showIntel.facebook?.data })}
                          />
                        </div>
                        <div>
                          <div className="text-gray-400 mb-1">TikTok</div>
                          <ScrapeButton
                            hasData={Boolean(showIntel.tiktok?.data)}
                            creditCost={CREDIT_COSTS.tiktok ?? 2}
                            onScrape={() => runScrape(show, 'tiktok')}
                            onRescrape={() => runScrape(show, 'tiktok')}
                            onViewData={() => setDrawer({ showId: show.showId, title: asText(show.title) || 'Unknown', source: 'tiktok', data: showIntel.tiktok?.data })}
                          />
                        </div>
                        <div>
                          <div className="text-gray-400 mb-1">LinkedIn</div>
                          <ScrapeButton
                            hasData={Boolean(showIntel.linkedin?.data)}
                            creditCost={CREDIT_COSTS.linkedin_profile_only ?? 1}
                            onScrape={() => runScrape(show, 'linkedin')}
                            onRescrape={() => runScrape(show, 'linkedin')}
                            onViewData={() => setDrawer({ showId: show.showId, title: asText(show.title) || 'Unknown', source: 'linkedin', data: showIntel.linkedin?.data })}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {createListModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-md">
                <h3 className="text-lg font-semibold mb-2">Create Podcast List</h3>
                <input
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="Podcast Outreach"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-white mb-4"
                />
                <div className="flex items-center justify-end gap-2">
                  <button
                    className="px-4 py-2 rounded bg-gray-700 text-sm"
                    onClick={() => {
                      setCreateListModalOpen(false);
                      setNewListName('');
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 rounded bg-red-600 text-sm"
                    onClick={() => {
                      handleCreatePodcastList().catch((e) => {
                        console.error(e);
                        alert(e instanceof Error ? e.message : 'Failed to create podcast list');
                      });
                    }}
                  >
                    Create List
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {drawer && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6">
          <div className="bg-gray-900 border border-gray-800 rounded-xl max-w-4xl w-full max-h-[85vh] overflow-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase text-gray-500">{drawer.source}</div>
                <div className="text-lg font-semibold">{drawer.title}</div>
              </div>
              <button className="text-gray-400" onClick={() => setDrawer(null)}>Close</button>
            </div>
            {drawer.source === 'podcast' ? (
              (() => {
                const summary = summarizePodcastData(drawer.data);
                const latest = summary.latestEpisode;
                return (
                  <div className="space-y-5">
                    <div className="bg-gradient-to-br from-fuchsia-900/40 to-gray-900 border border-fuchsia-700/40 rounded-xl p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
                          {summary.image ? (
                            <img src={summary.image} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-xs text-gray-400">Artwork</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-xs uppercase tracking-wide text-fuchsia-300/80">Podcast</p>
                          <h4 className="text-lg font-semibold text-white">
                            {summary.showTitle || drawer.title}
                          </h4>
                          <p className="text-sm text-gray-400 mt-1">
                            {summary.publisher || 'Publisher unknown'}
                          </p>
                          {summary.description && (
                            <p className="text-sm text-gray-300 mt-2 line-clamp-3">
                              {stripHtml(summary.description)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                      <p className="text-xs uppercase text-gray-500 mb-3">Latest Episode</p>
                      {!latest ? (
                        <p className="text-sm text-gray-400">No episode captured.</p>
                      ) : (
                        <div className="bg-gray-800/60 rounded-lg p-3">
                          <div className="text-sm text-gray-200 font-semibold">
                            {latest.title || 'Episode'}
                          </div>
                          {latest.duration && (
                            <div className="text-xs text-gray-400 mt-1">Duration: {latest.duration}</div>
                          )}
                          {latest.description && (
                            <div className="text-xs text-gray-300 mt-2 line-clamp-3">
                              {stripHtml(latest.description)}
                            </div>
                          )}
                          {latest.episodeUrl && (
                            <a className="text-xs text-fuchsia-300 hover:text-fuchsia-200 mt-2 inline-block" href={latest.episodeUrl} target="_blank" rel="noreferrer">
                              Listen
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()
            ) : drawer.source === 'website' ? (
              (() => {
                const summary = summarizeWebsiteData(drawer.data);
                return (
                  <div className="space-y-5">
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                      <p className="text-xs uppercase tracking-wide text-gray-400">Website Overview</p>
                      <h4 className="text-lg font-semibold text-white mt-1">{summary.title || drawer.title}</h4>
                      {summary.description && (
                        <p className="text-sm text-gray-300 mt-2 line-clamp-3">{summary.description}</p>
                      )}
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-300">
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Pages</span>
                          <div>{summary.pagesCount}</div>
                        </div>
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Phones</span>
                          <div>{summary.phones?.[0] ?? 'Unknown'}</div>
                        </div>
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Address</span>
                          <div className="line-clamp-2">{summary.address || 'Unknown'}</div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                      <pre className="text-xs text-gray-300">
                        {JSON.stringify(drawer.data, null, 2)}
                      </pre>
                    </div>
                  </div>
                );
              })()
            ) : drawer.source === 'linkedin' ? (
              (() => {
                const summary = summarizeLinkedinData(drawer.data);
                return (
                  <div className="space-y-5">
                    <div className="bg-gradient-to-br from-sky-900/40 to-gray-900 border border-sky-700/40 rounded-xl p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-lg bg-gray-800/80 flex items-center justify-center overflow-hidden">
                          {summary.logo ? (
                            <img src={summary.logo} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-xs text-gray-400">Logo</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-xs uppercase tracking-wide text-sky-300/80">LinkedIn Company</p>
                          <h4 className="text-lg font-semibold text-white">
                            {summary.name || drawer.title}
                          </h4>
                          <p className="text-sm text-gray-400 mt-1">
                            {summary.description || 'No description found.'}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {summary.employeeCount !== undefined && (
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-sky-600 text-white">
                              {summary.employeeCount} employees
                            </span>
                          )}
                          {summary.size && (
                            <span className="text-xs text-sky-200">{summary.size}</span>
                          )}
                        </div>
                      </div>
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-300">
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Industry</span>
                          <div>{summary.industry || 'Unknown'}</div>
                        </div>
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Headquarters</span>
                          <div>{summary.headquarters || 'Unknown'}</div>
                        </div>
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Website</span>
                          <div>{summary.website || 'Unknown'}</div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                        <p className="text-xs uppercase text-gray-500 mb-2">Specialties</p>
                        <div className="flex flex-wrap gap-2">
                          {summary.specialties.length ? (
                            summary.specialties.map((sp) => (
                              <span key={sp} className="px-2 py-1 rounded bg-gray-800 text-xs text-gray-300">
                                {sp}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-500">No specialties listed</span>
                          )}
                        </div>
                      </div>
                      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                        <p className="text-xs uppercase text-gray-500 mb-2">Similar Pages</p>
                        <div className="space-y-2">
                          {summary.similar.length ? (
                            summary.similar.map((s) => (
                              <div key={s.name} className="text-xs text-gray-300">
                                {s.link ? (
                                  <a className="text-sky-300 hover:text-sky-200" href={s.link} target="_blank" rel="noreferrer">
                                    {s.name}
                                  </a>
                                ) : (
                                  s.name
                                )}
                              </div>
                            ))
                          ) : (
                            <span className="text-xs text-gray-500">No similar pages</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                      <p className="text-xs uppercase text-gray-500 mb-3">Recent Posts</p>
                      {summary.posts.length === 0 ? (
                        <p className="text-sm text-gray-400">No posts captured.</p>
                      ) : (
                        <div className="space-y-3">
                          {summary.posts.map((post, idx) => (
                            <div key={`${post.url}-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                              <div className="text-xs text-gray-400">
                                {post.date ? new Date(post.date).toLocaleDateString() : 'No date'}
                              </div>
                              <div className="text-sm text-gray-200 mt-1 line-clamp-3">
                                {post.text || 'Post content unavailable'}
                              </div>
                              <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                                {post.likes !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {post.likes}</span>
                                )}
                                {post.comments !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Comments {post.comments}</span>
                                )}
                                {post.shares !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Shares {post.shares}</span>
                                )}
                                {post.views !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Views {post.views}</span>
                                )}
                              </div>
                              {post.url && (
                                <a className="text-xs text-sky-300 hover:text-sky-200 mt-2 inline-block" href={post.url} target="_blank" rel="noreferrer">
                                  View post
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                      <p className="text-xs uppercase text-gray-500 mb-3">Featured Employees</p>
                      {summary.employees.length === 0 ? (
                        <p className="text-sm text-gray-400">No employees captured.</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {summary.employees.map((emp) => (
                            <div key={emp.name} className="flex items-center gap-3 bg-gray-800/60 rounded-lg p-3">
                              <div className="w-10 h-10 rounded-full bg-gray-700 overflow-hidden">
                                {emp.image ? <img src={emp.image} alt="" className="w-full h-full object-cover" /> : null}
                              </div>
                              <div className="text-sm text-gray-200">
                                {emp.link ? (
                                  <a className="text-sky-300 hover:text-sky-200" href={emp.link} target="_blank" rel="noreferrer">
                                    {emp.name}
                                  </a>
                                ) : (
                                  emp.name
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()
            ) : drawer.source === 'facebook' ? (
              (() => {
                const summary = summarizeFacebookData(drawer.data);
                return (
                  <div className="space-y-5">
                    <div className="bg-gradient-to-br from-blue-900/40 to-gray-900 border border-blue-700/40 rounded-xl p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
                          {summary.profilePictureUrl ? (
                            <img src={summary.profilePictureUrl} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-xs text-gray-400">Logo</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-xs uppercase tracking-wide text-blue-300/80">Facebook Page</p>
                          <h4 className="text-lg font-semibold text-white">
                            {summary.title || drawer.title}
                          </h4>
                          <p className="text-sm text-gray-400 mt-1">
                            {summary.intro || 'No intro found.'}
                          </p>
                        </div>
                      </div>
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-300">
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Category</span>
                          <div>{summary.category || 'Unknown'}</div>
                        </div>
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Followers</span>
                          <div>{summary.followers ?? summary.likes ?? 'Unknown'}</div>
                        </div>
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Phone</span>
                          <div>{summary.phone || 'Unknown'}</div>
                        </div>
                      </div>
                      {summary.pageUrl && (
                        <a className="text-xs text-blue-300 hover:text-blue-200 mt-2 inline-block" href={summary.pageUrl} target="_blank" rel="noreferrer">
                          View page
                        </a>
                      )}
                    </div>

                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                      <p className="text-xs uppercase text-gray-500 mb-3">Recent Posts</p>
                      {summary.posts.length === 0 ? (
                        <p className="text-sm text-gray-400">No posts captured.</p>
                      ) : (
                        <div className="space-y-3">
                          {summary.posts.map((post, idx) => (
                            <div key={`${post.url}-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                              <div className="text-xs text-gray-400">
                                {post.date ? new Date(post.date).toLocaleDateString() : 'No date'}
                              </div>
                              <div className="text-sm text-gray-200 mt-1 line-clamp-3">
                                {post.text || 'Post content unavailable'}
                              </div>
                              <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                                {post.likes !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {post.likes}</span>
                                )}
                                {post.comments !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Comments {post.comments}</span>
                                )}
                                {post.shares !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Shares {post.shares}</span>
                                )}
                                {post.views !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Views {post.views}</span>
                                )}
                              </div>
                              {post.url && (
                                <a className="text-xs text-blue-300 hover:text-blue-200 mt-2 inline-block" href={post.url} target="_blank" rel="noreferrer">
                                  View post
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()
            ) : drawer.source === 'instagram' ? (
              (() => {
                const summary = summarizeInstagramData(drawer.data);
                return (
                  <div className="space-y-5">
                    <div className="bg-gradient-to-br from-pink-900/40 to-gray-900 border border-pink-700/40 rounded-xl p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
                          {summary.avatar ? (
                            <img src={summary.avatar} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-xs text-gray-400">Avatar</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-xs uppercase tracking-wide text-pink-300/80">Instagram Profile</p>
                          <h4 className="text-lg font-semibold text-white">
                            {summary.fullName || drawer.title}
                          </h4>
                          <p className="text-sm text-gray-400 mt-1">
                            {summary.bio || 'No bio found.'}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {summary.isVerified && (
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-pink-600 text-white">
                              Verified
                            </span>
                          )}
                          {summary.username && (
                            <span className="text-xs text-pink-200">@{summary.username}</span>
                          )}
                        </div>
                      </div>
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs text-gray-300">
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Followers</span>
                          <div>{summary.followers ?? 'Unknown'}</div>
                        </div>
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Following</span>
                          <div>{summary.following ?? 'Unknown'}</div>
                        </div>
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Posts</span>
                          <div>{summary.postsCount ?? 'Unknown'}</div>
                        </div>
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Website</span>
                          <div>{summary.website ?? 'Unknown'}</div>
                        </div>
                      </div>
                      {summary.profileUrl && (
                        <a className="text-xs text-pink-300 hover:text-pink-200 mt-2 inline-block" href={summary.profileUrl} target="_blank" rel="noreferrer">
                          View profile
                        </a>
                      )}
                    </div>

                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                      <p className="text-xs uppercase text-gray-500 mb-3">Recent Posts</p>
                      {summary.recentPosts.length === 0 ? (
                        <p className="text-sm text-gray-400">No posts captured.</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {summary.recentPosts.map((post, idx) => (
                            <div key={`${post.url}-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                              <div className="text-xs text-gray-400">
                                {post.date ? new Date(post.date).toLocaleDateString() : 'No date'}
                              </div>
                              <div className="text-sm text-gray-200 mt-1 line-clamp-3">
                                {post.caption || 'Post content unavailable'}
                              </div>
                              {(post.image || post.video) && (
                                <div className="mt-2 text-xs text-pink-200">
                                  {post.video ? 'Video' : 'Image post'}
                                </div>
                              )}
                              <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                                {post.likes !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {post.likes}</span>
                                )}
                                {post.comments !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Comments {post.comments}</span>
                                )}
                                {post.views !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Views {post.views}</span>
                                )}
                              </div>
                              {post.url && (
                                <a className="text-xs text-pink-300 hover:text-pink-200 mt-2 inline-block" href={post.url} target="_blank" rel="noreferrer">
                                  View post
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()
            ) : drawer.source === 'tiktok' ? (
              (() => {
                const summary = summarizeTikTokData(drawer.data);
                return (
                  <div className="space-y-5">
                    <div className="bg-gradient-to-br from-cyan-900/40 to-gray-900 border border-cyan-700/40 rounded-xl p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
                          {summary.avatar ? (
                            <img src={summary.avatar} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-xs text-gray-400">Avatar</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-xs uppercase tracking-wide text-cyan-300/80">TikTok Profile</p>
                          <h4 className="text-lg font-semibold text-white">
                            {summary.fullName || drawer.title}
                          </h4>
                          <p className="text-sm text-gray-400 mt-1">
                            {summary.bio || 'No bio found.'}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {summary.isVerified && (
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-cyan-600 text-white">
                              Verified
                            </span>
                          )}
                          {summary.username && (
                            <span className="text-xs text-cyan-200">@{summary.username}</span>
                          )}
                        </div>
                      </div>
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs text-gray-300">
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Followers</span>
                          <div>{summary.followers ?? 'Unknown'}</div>
                        </div>
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Following</span>
                          <div>{summary.following ?? 'Unknown'}</div>
                        </div>
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Videos</span>
                          <div>{summary.videos ?? 'Unknown'}</div>
                        </div>
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Hearts</span>
                          <div>{summary.hearts ?? 'Unknown'}</div>
                        </div>
                      </div>
                      {summary.website && (
                        <div className="mt-3 text-xs text-gray-400">Website: {summary.website}</div>
                      )}
                      {summary.profileUrl && (
                        <a className="text-xs text-cyan-300 hover:text-cyan-200 mt-2 inline-block" href={summary.profileUrl} target="_blank" rel="noreferrer">
                          View profile
                        </a>
                      )}
                    </div>

                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                      <p className="text-xs uppercase text-gray-500 mb-3">Recent Posts</p>
                      {summary.recentPosts.length === 0 ? (
                        <p className="text-sm text-gray-400">No posts captured.</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {summary.recentPosts.map((post, idx) => (
                            <div key={`${post.url}-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                              <div className="text-xs text-gray-400">
                                {post.date ? new Date(post.date).toLocaleDateString() : 'No date'}
                              </div>
                              <div className="text-sm text-gray-200 mt-1 line-clamp-3">
                                {post.caption || 'Post content unavailable'}
                              </div>
                              {(post.image || post.duration) && (
                                <div className="mt-2 text-xs text-cyan-200">
                                  {post.duration ? `Video (${post.duration}s)` : 'Image post'}
                                </div>
                              )}
                              {post.hashtags && post.hashtags.length > 0 && (
                                <div className="mt-2 text-[11px] text-cyan-200">
                                  {post.hashtags.slice(0, 4).map((tag) => `#${tag}`).join(' ')}
                                </div>
                              )}
                              <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                                {post.likes !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {post.likes}</span>
                                )}
                                {post.comments !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Comments {post.comments}</span>
                                )}
                                {post.shares !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Shares {post.shares}</span>
                                )}
                                {post.views !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Views {post.views}</span>
                                )}
                              </div>
                              {post.url && (
                                <a className="text-xs text-cyan-300 hover:text-cyan-200 mt-2 inline-block" href={post.url} target="_blank" rel="noreferrer">
                                  View post
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()
            ) : drawer.source === 'youtube' ? (
              (() => {
                const summary = summarizeYouTubeData(drawer.data);
                return (
                  <div className="space-y-5">
                    <div className="bg-gradient-to-br from-red-900/40 to-gray-900 border border-red-700/40 rounded-xl p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
                          {summary.channelAvatar ? (
                            <img src={summary.channelAvatar} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-xs text-gray-400">Avatar</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-xs uppercase tracking-wide text-red-300/80">YouTube Channel</p>
                          <h4 className="text-lg font-semibold text-white">
                            {summary.channelName || drawer.title}
                          </h4>
                          <p className="text-sm text-gray-400 mt-1">
                            {summary.channelLocation || 'Location unknown'}
                          </p>
                        </div>
                      </div>
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-300">
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Subscribers</span>
                          <div>{summary.subscribers ?? 'Unknown'}</div>
                        </div>
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Total Views</span>
                          <div>{summary.totalViews ?? 'Unknown'}</div>
                        </div>
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Videos</span>
                          <div>{summary.totalVideos ?? 'Unknown'}</div>
                        </div>
                      </div>
                      {summary.channelUrl && (
                        <a className="text-xs text-red-300 hover:text-red-200 mt-2 inline-block" href={summary.channelUrl} target="_blank" rel="noreferrer">
                          View channel
                        </a>
                      )}
                    </div>

                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                      <p className="text-xs uppercase text-gray-500 mb-3">Recent Videos</p>
                      {summary.recentVideos.length === 0 ? (
                        <p className="text-sm text-gray-400">No videos captured.</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {summary.recentVideos.map((video, idx) => (
                            <div key={`${video.url}-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                              <div className="text-xs text-gray-400">
                                {video.date ? new Date(video.date).toLocaleDateString() : 'No date'}
                              </div>
                              <div className="text-sm text-gray-200 mt-1 line-clamp-2">
                                {video.title || video.text || 'Video'}
                              </div>
                              {video.thumbnail && (
                                <div className="mt-2 text-xs text-red-200">Thumbnail available</div>
                              )}
                              <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                                {video.views !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Views {video.views}</span>
                                )}
                                {video.likes !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {video.likes}</span>
                                )}
                                {video.comments !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Comments {video.comments}</span>
                                )}
                              </div>
                              {video.url && (
                                <a className="text-xs text-red-300 hover:text-red-200 mt-2 inline-block" href={video.url} target="_blank" rel="noreferrer">
                                  View video
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()
            ) : drawer.source === 'x' ? (
              (() => {
                const summary = summarizeXData(drawer.data);
                return (
                  <div className="space-y-5">
                    <div className="bg-gradient-to-br from-slate-800/60 to-gray-900 border border-slate-600/40 rounded-xl p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
                          {summary.avatar ? (
                            <img src={summary.avatar} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-xs text-gray-400">Avatar</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-xs uppercase tracking-wide text-slate-300/80">X Profile</p>
                          <h4 className="text-lg font-semibold text-white">
                            {summary.profileName || drawer.title}
                          </h4>
                          {summary.profileHandle && (
                            <p className="text-sm text-gray-400 mt-1">@{summary.profileHandle}</p>
                          )}
                          {summary.bio && (
                            <p className="text-sm text-gray-300 mt-2 line-clamp-3">{summary.bio}</p>
                          )}
                        </div>
                      </div>
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-300">
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Followers</span>
                          <div>{summary.followers ?? 'Unknown'}</div>
                        </div>
                        <div className="bg-gray-900/60 rounded-lg p-2">
                          <span className="text-gray-500">Following</span>
                          <div>{summary.following ?? 'Unknown'}</div>
                        </div>
                      </div>
                      {summary.profileUrl && (
                        <a className="text-xs text-slate-300 hover:text-slate-200 mt-2 inline-block" href={summary.profileUrl} target="_blank" rel="noreferrer">
                          View profile
                        </a>
                      )}
                    </div>

                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                      <p className="text-xs uppercase text-gray-500 mb-3">Recent Posts</p>
                      {summary.recentPosts.length === 0 ? (
                        <p className="text-sm text-gray-400">No posts captured.</p>
                      ) : (
                        <div className="space-y-3">
                          {summary.recentPosts.map((post, idx) => (
                            <div key={`${post.url}-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                              <div className="text-xs text-gray-400">
                                {post.date ? new Date(post.date).toLocaleDateString() : 'No date'}
                              </div>
                              {post.media && (
                                <div className="mt-2">
                                  <img src={post.media} alt="" className="w-full max-w-sm rounded-md border border-gray-700" />
                                </div>
                              )}
                              <div className="text-sm text-gray-200 mt-1 line-clamp-3">
                                {post.text || 'Post content unavailable'}
                              </div>
                              <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                                {post.likes !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {post.likes}</span>
                                )}
                                {post.replies !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Replies {post.replies}</span>
                                )}
                                {post.reposts !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Reposts {post.reposts}</span>
                                )}
                                {post.quotes !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Quotes {post.quotes}</span>
                                )}
                                {post.views !== undefined && (
                                  <span className="px-2 py-0.5 rounded bg-gray-900/60">Views {post.views}</span>
                                )}
                              </div>
                              {post.url && (
                                <a className="text-xs text-slate-300 hover:text-slate-200 mt-2 inline-block" href={post.url} target="_blank" rel="noreferrer">
                                  View post
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-xs text-gray-300">
                  {JSON.stringify(drawer.data, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      {activeEpisodeId && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6">
          <div className="bg-gray-900 border border-gray-800 rounded-xl max-w-5xl w-full max-h-[85vh] overflow-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase text-gray-500">Episode Details</div>
                <div className="text-lg font-semibold">{activeEpisodeId}</div>
              </div>
              <button className="text-gray-400" onClick={() => setActiveEpisodeId(null)}>Close</button>
            </div>
            {episodeDetails[activeEpisodeId]?.loading ? (
              <div className="text-sm text-gray-400">Loading episode details…</div>
            ) : episodeDetails[activeEpisodeId]?.error ? (
              <div className="text-sm text-rose-300">{episodeDetails[activeEpisodeId]?.error}</div>
            ) : (
              <EpisodeInsights
                source={episodeDetails[activeEpisodeId]?.data ?? {}}
                transcript={episodeDetails[activeEpisodeId]?.transcript ?? {}}
                showRaw
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
